import React, {
  RefObject,
  useLayoutEffect,
  useMemo,
  useRef,
  useState,
} from 'react'
import {
  ArrowLeft,
  ChevronDown,
  ClipboardPaste,
  Clock3,
  ContactRound,
  Delete,
  Forward,
  LoaderCircle,
  Mic,
  MicOff,
  Pause,
  Phone,
  PhoneCall,
  PhoneIncoming,
  PhoneOff,
  Play,
  RefreshCw,
  Search,
  UserRound,
  UsersRound,
  X,
  type LucideIcon,
} from 'lucide-react'
import {
  BsTelephoneInboundFill,
  BsTelephoneOutboundFill,
  BsTelephoneXFill,
} from 'react-icons/bs'
import { MdDialpad, MdHistory } from 'react-icons/md'
import { PiAddressBook } from 'react-icons/pi'
import { useDispatcher } from '@/providers/DispatcherProvider'
import {
  type SoftphoneActiveCall,
  type SoftphonePanelTab,
  useSoftphone,
} from '@/providers/SoftphoneProvider'
import type { SoftphoneRealtimeStatus } from '@/services/softphone/realtimeSocket'
import {
  useActiveSoftphoneCalls,
  useSoftphoneCallHistory,
  useSoftphoneQueueStatus,
} from '@/hooks/api/useSoftphone'
import { usePatientEntityLookups } from '@/hooks/api/usePatientEntities'
import { useGetAllDispatchers } from '@/hooks/api/useDispatcher'
import { useShifts } from '@/hooks/api/useShifts'
import { useAllResponders } from '@/hooks/api/useResponder'
import { useAllHospitals } from '@/hooks/api/useHospital'
import { useAmbulanceProviders } from '@/hooks/api/useAmbulance'
import { useDebounce } from '@/hooks/useDebounce'
import type {
  SoftphoneCallDirection,
  SoftphoneCallSession,
  SoftphoneQueueAgent,
  SoftphoneQueueStatus,
} from '@/types/api/softphone'
import type { Dispatcher } from '@/types/api/dispatcher'
import type { PatientEntity } from '@/types/api/patients'
import type { Responder } from '@/types/api/responders'
import { IconType } from 'react-icons/lib'

type HistoryFilter = 'all' | 'incoming' | 'outgoing' | 'missed'
type HistoryCallStatusFilter =
  'all' | 'missed' | 'completed' | 'declined' | 'transferred'
type HistoryTimeFilter =
  | 'all'
  | 'today'
  | 'yesterday'
  | 'last7Days'
  | 'last2Weeks'
  | 'thisMonth'
  | 'custom'
type HistoryDispatcherFilter = 'all' | string
type ContactTab = 'all' | 'responders' | 'hospitals' | 'ambulances'

interface HistoryDropdownOption<TValue extends string = string> {
  value: TValue
  label: string
  status?: string
}

interface SoftphoneContact {
  id: string
  name: string
  phoneNumber?: string
  type: ContactTab
  subtitle?: string
  status?: string
  responder?: Responder
}

interface SoftphoneContactLookup {
  byId: Map<string, string>
  byPhone: Map<string, string>
}

const topTabs: Array<{
  key: SoftphonePanelTab
  label: string
  Icon: LucideIcon | IconType
}> = [
  { key: 'history', label: 'Call History', Icon: MdHistory },
  { key: 'dialpad', label: 'Dialpad', Icon: MdDialpad },
  { key: 'contacts', label: 'Contacts', Icon: PiAddressBook },
]

const historyFilters: Array<{ key: HistoryFilter; label: string }> = [
  { key: 'all', label: 'All' },
  { key: 'incoming', label: 'Incoming' },
  { key: 'outgoing', label: 'Outgoing' },
  { key: 'missed', label: 'Missed' },
]

const historyStatusFilterOptions: Array<
  HistoryDropdownOption<HistoryCallStatusFilter>
> = [
  { value: 'all', label: 'All statuses' },
  { value: 'missed', label: 'Missed' },
  { value: 'completed', label: 'Completed' },
  { value: 'declined', label: 'Declined' },
  { value: 'transferred', label: 'Transferred' },
]

const historyTimeFilterOptions: Array<
  HistoryDropdownOption<HistoryTimeFilter>
> = [
  { value: 'all', label: 'All time' },
  { value: 'today', label: 'Today' },
  { value: 'yesterday', label: 'Yesterday' },
  { value: 'last7Days', label: 'Last 7 days' },
  { value: 'last2Weeks', label: 'Last 2 weeks' },
  { value: 'thisMonth', label: 'This Month' },
  { value: 'custom', label: 'Custom' },
]

const getRealtimeStatusMessage = (
  status: SoftphoneRealtimeStatus,
  error?: string | null
) => {
  switch (status) {
    case 'connected':
      return null
    case 'connecting':
      return 'Connecting softphone realtime events'
    case 'disconnected':
    case 'error':
      return 'Softphone realtime reconnecting'
    case 'unavailable':
      return error || 'Softphone realtime unavailable'
    case 'idle':
    default:
      return 'Preparing softphone realtime events'
  }
}

const humanizeSoftphoneMessage = (message?: string | null) => {
  if (!message) return null

  const normalized = message.toLowerCase()

  if (
    normalized.includes('sip invite rejected') &&
    (normalized.includes('404') || normalized.includes('cause=3'))
  ) {
    return 'Call route not found. The softphone tried the available route, but Asterisk does not have a matching destination.'
  }

  if (
    normalized.includes('sip invite rejected') &&
    (normalized.includes('480') ||
      normalized.includes('temporarily unavailable'))
  ) {
    return 'The callee is temporarily unavailable. Try again or use another contact route.'
  }

  if (
    normalized.includes('sip invite rejected') &&
    (normalized.includes('486') || normalized.includes('busy'))
  ) {
    return 'The callee is busy.'
  }

  if (
    normalized.includes('sip invite rejected') &&
    (normalized.includes('408') || normalized.includes('timeout'))
  ) {
    return 'The call timed out before the destination answered.'
  }

  if (normalized.includes('softphone is not connected')) {
    return 'Softphone is disconnected. Reconnect before placing a call.'
  }

  if (normalized.includes('softphone is still connecting')) {
    return 'Softphone is still connecting.'
  }

  if (normalized.includes('session does not exist')) {
    return 'The call session has already ended.'
  }

  if (normalized.includes('request failed with status code 502')) {
    return 'Call service is temporarily unavailable.'
  }

  if (normalized.includes('request failed with status code 504')) {
    return 'Call service timed out.'
  }

  if (normalized.includes('dispatcher is not on an active shift')) {
    return 'Softphone access is blocked because this dispatcher is not on an active shift.'
  }

  if (
    normalized.includes('failed to fetch') ||
    normalized.includes('network')
  ) {
    return 'Network connection lost. Check connectivity and try again.'
  }

  return message
}

const dialpadKeys = [
  { digit: '1', letters: '' },
  { digit: '2', letters: 'ABC' },
  { digit: '3', letters: 'DEF' },
  { digit: '4', letters: 'GHI' },
  { digit: '5', letters: 'JKL' },
  { digit: '6', letters: 'MNO' },
  { digit: '7', letters: 'PQRS' },
  { digit: '8', letters: 'TUV' },
  { digit: '9', letters: 'WXYZ' },
  { digit: '*', letters: '' },
  { digit: '0', letters: '+' },
  { digit: '#', letters: '' },
]

const SOFTPHONE_PANEL_GAP = 8
const SOFTPHONE_PANEL_WIDTH = 472
const SOFTPHONE_PANEL_MAX_HEIGHT = 880
const SOFTPHONE_VIEWPORT_MARGIN = 16

const formatDialpadNumber = (value: string) => {
  if (!value) return 'Enter Number'

  const compactValue = value.replace(/\s+/g, '')
  const groups: string[] = []
  let cursor = 0
  const groupPattern = [4, 3, 4]

  groupPattern.forEach(size => {
    if (cursor >= compactValue.length) return
    groups.push(compactValue.slice(cursor, cursor + size))
    cursor += size
  })

  if (cursor < compactValue.length) {
    groups.push(compactValue.slice(cursor))
  }

  return groups.join(' ')
}

const formatPersonName = (
  firstName?: string | null,
  lastName?: string | null,
  fallback?: string
) => {
  const name = [firstName, lastName].filter(Boolean).join(' ').trim()
  return name || fallback || ''
}

const getOptionalStringField = (source: unknown, field: string) => {
  if (!source || typeof source !== 'object') return undefined

  const value = (source as Record<string, unknown>)[field]
  return typeof value === 'string' ? value : undefined
}

const normalizePhoneForLookup = (value?: string | null) =>
  String(value || '').replace(/\D/g, '')

const getEntityPrecinctIds = (source: unknown) => {
  if (!source || typeof source !== 'object') return []

  const record = source as Record<string, unknown>
  const ids = new Set<string>()
  const addId = (value: unknown) => {
    if (typeof value === 'string' && value.trim()) ids.add(value.trim())
  }

  addId(record.precinct_id)
  addId(record.precinctId)
  addId(record.dispatch_center_id)
  addId(record.dispatch_center_precinct_id)

  if (Array.isArray(record.working_precincts)) {
    record.working_precincts.forEach(addId)
  }

  if (Array.isArray(record.coverage_by_precinct)) {
    record.coverage_by_precinct.forEach(item => {
      if (item && typeof item === 'object') {
        addId((item as Record<string, unknown>).precinct_id)
      }
    })
  }

  return Array.from(ids)
}

const matchesCurrentPrecinct = (
  source: unknown,
  currentPrecinctId?: string | null
) => {
  if (!currentPrecinctId) return true

  const precinctIds = getEntityPrecinctIds(source)
  return precinctIds.includes(currentPrecinctId)
}

const getDispatchersFromResponse = (dispatchersData: unknown) => {
  if (!dispatchersData || typeof dispatchersData !== 'object') return []

  const data = dispatchersData as {
    data?: { dispatchers?: Dispatcher[] }
    dispatchers?: Dispatcher[]
    items?: Dispatcher[]
  }

  return data.data?.dispatchers || data.dispatchers || data.items || []
}

const getDispatcherDisplayName = (dispatcher: Dispatcher) =>
  formatPersonName(
    dispatcher.first_name,
    dispatcher.last_name,
    dispatcher.email || dispatcher.phone_number || 'Dispatcher'
  )

const getPhoneLookupKeys = (value?: string | null) => {
  const digits = normalizePhoneForLookup(value)
  if (!digits) return []

  const keys = new Set<string>([digits])
  if (digits.length >= 10) keys.add(digits.slice(-10))
  if (digits.startsWith('234') && digits.length > 10) {
    keys.add(`0${digits.slice(3)}`)
  }
  if (digits.startsWith('0') && digits.length > 1) {
    keys.add(`234${digits.slice(1)}`)
  }

  return Array.from(keys)
}

const getSipUserPart = (value?: string | null) => {
  const normalized = String(value || '')
    .trim()
    .replace(/^PJSIP\//i, '')
    .replace(/^sip:/i, '')
  return normalized.split('@')[0] || ''
}

const addContactLookupEntry = (
  lookup: SoftphoneContactLookup,
  {
    id,
    phoneNumber,
    name,
  }: {
    id?: string | null
    phoneNumber?: string | null
    name?: string | null
  }
) => {
  const cleanName = String(name || '').trim()
  if (!cleanName) return

  if (id) lookup.byId.set(String(id), cleanName)
  getPhoneLookupKeys(phoneNumber).forEach(key => {
    lookup.byPhone.set(key, cleanName)
  })
}

const isGenericSoftphoneName = (value?: string | null) => {
  const normalized = String(value || '')
    .trim()
    .toLowerCase()
  return (
    !normalized ||
    normalized === 'unknown' ||
    normalized === 'unknown caller' ||
    normalized === 'era dispatcher' ||
    normalized === 'toll-free'
  )
}

const resolvePhoneContactName = (
  value: string | null | undefined,
  lookup: SoftphoneContactLookup
) => {
  const sipUser = getSipUserPart(value)
  const candidates = [sipUser, value]

  for (const candidate of candidates) {
    for (const key of getPhoneLookupKeys(candidate)) {
      const match = lookup.byPhone.get(key)
      if (match) return match
    }
  }

  return undefined
}

const getActiveCallContactDisplay = (
  call: SoftphoneActiveCall,
  lookup: SoftphoneContactLookup
) => {
  const lookupName =
    resolvePhoneContactName(call.phoneNumber, lookup) ||
    resolvePhoneContactName(call.target, lookup)
  const displayName = isGenericSoftphoneName(call.displayName)
    ? undefined
    : call.displayName
  const targetName = isGenericSoftphoneName(call.target)
    ? undefined
    : call.target
  const rawTarget =
    call.phoneNumber ||
    (!isGenericSoftphoneName(call.target) ? call.target : undefined)
  const name =
    lookupName || displayName || targetName || rawTarget || 'Unknown Caller'
  const detail =
    rawTarget && rawTarget !== name && rawTarget !== lookupName
      ? rawTarget
      : undefined

  return { name, detail }
}

const getDirection = (call: SoftphoneCallSession): SoftphoneCallDirection => {
  if (call.direction === 'incoming' || call.direction === 'inbound') {
    return 'incoming'
  }

  if (call.direction === 'outgoing' || call.direction === 'outbound') {
    return 'outgoing'
  }

  if (call.status === 'missed') return 'incoming'
  if (call.to_type === 'pstn' || call.to_user_type === 'responder') {
    return 'outgoing'
  }
  if (call.to || call.dial_target || call.to_user_id) return 'outgoing'

  return 'incoming'
}

const resolveCallContactName = (
  call: SoftphoneCallSession,
  lookup: SoftphoneContactLookup
) => {
  const direction = getDirection(call)
  const idCandidates =
    direction === 'outgoing'
      ? [call.to_user_id, call.to, getSipUserPart(call.dial_target)]
      : [call.from_user_id, call.from, getSipUserPart(call.from)]

  for (const id of idCandidates) {
    const match = id ? lookup.byId.get(String(id)) : undefined
    if (match) return match
  }

  const phoneCandidates =
    direction === 'outgoing'
      ? [call.to, call.phone_number, call.dial_target, call.sip_uri]
      : [call.phone_number, call.from, call.caller_id, call.callerId]

  for (const phone of phoneCandidates) {
    const sipUser = getSipUserPart(phone)
    for (const key of getPhoneLookupKeys(sipUser || phone)) {
      const match = lookup.byPhone.get(key)
      if (match) return match
    }
  }

  return getDisplayTarget(call)
}

const resolveCallResponderName = (
  call: SoftphoneCallSession,
  lookup: SoftphoneContactLookup
) => {
  const assignedId = call.assigned_to_actor_id
  if (!assignedId) return ''

  return (
    lookup.byId.get(String(assignedId)) ||
    resolvePhoneContactName(call.assigned_to_phone_number, lookup) ||
    call.assigned_to_phone_number ||
    String(assignedId)
  )
}

const getDisplayTarget = (call: SoftphoneCallSession) => {
  if (getDirection(call) === 'outgoing') {
    return (
      call.to ||
      call.to_user_id ||
      call.dial_target ||
      call.sip_uri ||
      call.caller_id ||
      call.callerId ||
      'Unknown'
    )
  }

  return (
    call.phone_number ||
    call.caller_id ||
    call.callerId ||
    call.from ||
    call.from_user_id ||
    call.to ||
    call.to_user_id ||
    call.dial_target ||
    'Unknown'
  )
}

const getCallPhoneCandidates = (call: SoftphoneCallSession) => {
  const direction = getDirection(call)
  const candidates =
    direction === 'outgoing'
      ? [call.to, call.phone_number, call.dial_target, call.sip_uri]
      : [call.phone_number, call.from, call.caller_id, call.callerId]

  return candidates
    .flatMap(value => [value, getSipUserPart(value)])
    .filter((value): value is string => Boolean(value))
}

const getPhoneCandidateDisplayValue = (value?: string | null) => {
  if (!value) return ''

  const candidate = getSipUserPart(value) || value
  const trimmed = candidate.trim()
  if (!trimmed || /[a-z]/i.test(trimmed)) return ''

  const digits = normalizePhoneForLookup(trimmed)
  if (digits.length < 7) return ''

  return trimmed
}

const getCallableHistoryNumber = (call: SoftphoneCallSession) => {
  const candidates = getCallPhoneCandidates(call)

  for (const candidate of candidates) {
    const phoneNumber = getPhoneCandidateDisplayValue(candidate)
    if (phoneNumber) return phoneNumber
  }

  return ''
}

const getActiveCallPhoneCandidates = (call?: SoftphoneActiveCall | null) =>
  call ? [call.phoneNumber, call.target].filter(Boolean) : []

const getPatientDisplayName = (patient: PatientEntity) => {
  const names = patient.names
    .map(item => item.name)
    .filter(Boolean)
    .filter((name, index, all) => all.indexOf(name) === index)

  return names.join(' / ') || patient.display_name || 'Patient'
}

const getCallTime = (call: SoftphoneCallSession) => {
  const timestamp = Date.parse(call.started_at || call.created_at || '')
  return Number.isNaN(timestamp) ? 0 : timestamp
}

const getCallCorrelationTarget = (
  call: SoftphoneCallSession,
  lookup: SoftphoneContactLookup
) => {
  const direction = getDirection(call)
  const phoneCandidates =
    direction === 'outgoing'
      ? [call.to, call.phone_number, call.dial_target, call.sip_uri]
      : [call.phone_number, call.from, call.caller_id, call.callerId]

  for (const phone of phoneCandidates) {
    const sipUser = getSipUserPart(phone)
    const keys = getPhoneLookupKeys(sipUser || phone)
    if (keys.length > 0) return keys[0]
  }

  return resolveCallContactName(call, lookup).trim().toLowerCase()
}

const getNormalizedCallStatus = (status?: string) =>
  String(status || '').toLowerCase()

const hasPositiveCallDuration = (call: SoftphoneCallSession) => {
  const duration = call.duration_seconds ?? call.duration
  return typeof duration === 'number' && duration > 0
}

const isOutboundEndedCall = (call: SoftphoneCallSession) =>
  getDirection(call) === 'outgoing' &&
  ['cancelled', 'canceled'].includes(getNormalizedCallStatus(call.status)) &&
  hasPositiveCallDuration(call)

const getCallStatusRank = (call: SoftphoneCallSession) => {
  const status = getNormalizedCallStatus(call.status)
  if (isOutboundEndedCall(call)) return 5
  if (['completed', 'answered', 'held'].includes(status)) return 5
  if (['dialing', 'ringing', 'initiating'].includes(status)) return 4
  if (status === 'rejected') return 3
  if (['failed', 'cancelled', 'canceled', 'no_answer'].includes(status))
    return 2
  if (status === 'missed') return 1
  return 0
}

const isUnsuccessfulCallStatus = (status?: string) =>
  [
    'missed',
    'rejected',
    'failed',
    'cancelled',
    'canceled',
    'no_answer',
  ].includes(getNormalizedCallStatus(status))

const isSuccessfulCallStatus = (status?: string) =>
  ['completed', 'answered', 'held'].includes(getNormalizedCallStatus(status))

const isMissedHistoryCall = (call: SoftphoneCallSession) => {
  if (getDirection(call) !== 'incoming') return false

  const status = getNormalizedCallStatus(call.status)
  if (['missed', 'no_answer'].includes(status)) return true

  return (
    ['cancelled', 'canceled'].includes(status) &&
    !call.answered_at &&
    !hasPositiveCallDuration(call)
  )
}

const isLocalOnlyCall = (call: SoftphoneCallSession) =>
  call.call_id.startsWith('local-') || call.metadata?.localOnly === true

const shouldReplaceHistoryCall = (
  current: SoftphoneCallSession,
  next: SoftphoneCallSession
) => {
  if (isLocalOnlyCall(current) !== isLocalOnlyCall(next)) {
    return isLocalOnlyCall(current)
  }

  const statusDifference = getCallStatusRank(next) - getCallStatusRank(current)
  if (statusDifference !== 0) return statusDifference > 0

  return getCallTime(next) > getCallTime(current)
}

const dedupeHistoryCalls = (
  calls: SoftphoneCallSession[],
  lookup: SoftphoneContactLookup
) => {
  const deduped: SoftphoneCallSession[] = []

  calls.forEach(call => {
    const direction = getDirection(call)
    const target = getCallCorrelationTarget(call, lookup)
    const time = getCallTime(call)
    const duplicateIndex = deduped.findIndex(existing => {
      if (direction !== 'outgoing' || getDirection(existing) !== 'outgoing') {
        return false
      }
      if (!target || target !== getCallCorrelationTarget(existing, lookup)) {
        return false
      }

      return Math.abs(time - getCallTime(existing)) <= 45 * 1000
    })

    if (duplicateIndex === -1) {
      deduped.push(call)
      return
    }

    if (shouldReplaceHistoryCall(deduped[duplicateIndex], call)) {
      deduped[duplicateIndex] = call
    }
  })

  return deduped
}

const useSoftphoneContactLookup = (
  patientPhoneNumbers: Array<string | null | undefined> = [],
  patientLookupEnabled = true
) => {
  const { dispatcher } = useDispatcher()
  const currentPrecinctId = dispatcher?.precinct_id
  const { data: dispatchersData } = useGetAllDispatchers()
  const { data: respondersData } = useAllResponders({
    pageNumber: 1,
    numberPerPage: 100,
    precinct_id: currentPrecinctId,
  })
  const { data: hospitals = [] } = useAllHospitals({
    pageNumber: 1,
    numberPerPage: 100,
    precinct_id: currentPrecinctId,
  })
  const { data: ambulanceData } = useAmbulanceProviders({
    limit: 100,
    precinct_id: currentPrecinctId,
  })
  const patientLookupResult = usePatientEntityLookups(
    patientPhoneNumbers,
    patientLookupEnabled
  )
  const patients = useMemo(
    () =>
      Object.values(patientLookupResult.data?.patients_by_contact_number || {})
        .map(result => result.patient)
        .filter((patient): patient is PatientEntity => Boolean(patient)),
    [patientLookupResult.data?.patients_by_contact_number]
  )

  return useMemo<SoftphoneContactLookup>(() => {
    const lookup: SoftphoneContactLookup = {
      byId: new Map(),
      byPhone: new Map(),
    }

    if (dispatcher) {
      addContactLookupEntry(lookup, {
        id: dispatcher.dispatcher_id,
        phoneNumber: getOptionalStringField(dispatcher, 'phone_number'),
        name: formatPersonName(
          dispatcher.first_name,
          dispatcher.last_name,
          'Current Dispatcher'
        ),
      })
    }

    const dispatchers = getDispatchersFromResponse(dispatchersData)
    dispatchers.forEach((item: any) => {
      if (!matchesCurrentPrecinct(item, currentPrecinctId)) return

      addContactLookupEntry(lookup, {
        id: item.dispatcher_id || item.user_id,
        phoneNumber: item.phone_number,
        name: getDispatcherDisplayName(item),
      })
    })

    respondersData?.items
      ?.filter(responder =>
        matchesCurrentPrecinct(responder, currentPrecinctId)
      )
      .forEach(responder => {
        const responderName = formatPersonName(
          responder.first_name,
          responder.last_name,
          responder.phone_number || 'Responder'
        )

        addContactLookupEntry(lookup, {
          id: responder.responder_id,
          phoneNumber: responder.phone_number,
          name: responderName,
        })
        addContactLookupEntry(lookup, {
          phoneNumber: getOptionalStringField(
            responder,
            'alternative_phone_number'
          ),
          name: responderName,
        })
      })

    hospitals
      .filter((hospital: any) =>
        matchesCurrentPrecinct(hospital, currentPrecinctId)
      )
      .forEach((hospital: any) => {
        addContactLookupEntry(lookup, {
          id: hospital.hospital_id || hospital.id,
          phoneNumber:
            hospital.phoneNumber ||
            hospital.phone_number ||
            hospital.contact?.phone ||
            hospital.contact_phone,
          name: hospital.name || hospital.hospital_name || 'Hospital',
        })
      })

    const ambulanceProviders = Array.isArray((ambulanceData as any)?.items)
      ? (ambulanceData as any).items
      : Array.isArray((ambulanceData as any)?.ambulance_providers)
        ? (ambulanceData as any).ambulance_providers
        : Array.isArray((ambulanceData as any)?.providers)
          ? (ambulanceData as any).providers
          : Array.isArray(ambulanceData)
            ? ambulanceData
            : []
    ambulanceProviders
      .filter((provider: any) =>
        matchesCurrentPrecinct(provider, currentPrecinctId)
      )
      .forEach((provider: any) => {
        addContactLookupEntry(lookup, {
          id:
            provider.provider_id ||
            provider.ambulance_provider_id ||
            provider.id,
          phoneNumber:
            provider.contact_phone_number ||
            provider.phone_number ||
            provider.contact_phone ||
            provider.dispatch_phone ||
            provider.dispatch_phone_number ||
            provider.contact?.phone,
          name:
            provider.provider_name ||
            provider.company_name ||
            provider.name ||
            'Ambulance Provider',
        })
      })

    patients.forEach(patient => {
      const patientName = getPatientDisplayName(patient)
      const patientNumbers = [
        patient.contact_number,
        patient.identifier,
        ...(patient.contact_numbers || []),
      ]

      patientNumbers.forEach(phoneNumber => {
        addContactLookupEntry(lookup, {
          phoneNumber,
          name: patientName,
        })
      })
    })

    return lookup
  }, [
    ambulanceData,
    currentPrecinctId,
    dispatcher,
    dispatchersData,
    hospitals,
    patients,
    respondersData?.items,
  ])
}

const getCallCategory = (call: SoftphoneCallSession) => {
  if (call.to_type === 'pstn') return 'PSTN'
  if (call.to_user_type === 'responder') return 'Responder'
  if (call.to_type === 'user') return 'SIP'
  return getDirection(call) === 'outgoing' ? 'Outgoing' : 'Incoming'
}

const getCallStatusLabel = (call: SoftphoneCallSession) => {
  if (isOutboundEndedCall(call)) return 'Ended'

  const status = getNormalizedCallStatus(call.status)
  switch (status) {
    case 'initiating':
      return 'Starting'
    case 'dialing':
      return 'Dialing'
    case 'ringing':
      return 'Ringing'
    case 'answered':
      return 'Answered'
    case 'held':
      return 'Held'
    case 'completed':
      return 'Complete'
    case 'rejected':
      return 'Rejected'
    case 'missed':
      return 'Missed'
    case 'cancelled':
    case 'canceled':
      return 'Cancelled'
    case 'no_answer':
      return 'No answer'
    case 'failed':
      return 'Failed'
    default:
      return status ? status.replace(/_/g, ' ') : 'Logged'
  }
}

/*
const getCallCategoryClasses = (call: SoftphoneCallSession) => {
  if (isUnsuccessfulCallStatus(call.status)) {
    return 'border-[#ffd6dc] bg-[#fff6f7] text-[#d83a4a]'
  }
  if (isSuccessfulCallStatus(call.status)) {
    return 'border-[#d9f3e4] bg-[#f3fff8] text-[#0d8f45]'
  }
  if (call.to_type === 'pstn') {
    return 'border-[#d8e8ff] bg-[#f4f8ff] text-primary'
  }
  if (call.to_user_type === 'responder') {
    return 'border-[#d9f3e4] bg-[#f3fff8] text-[#0d8f45]'
  }
  return 'border-[#e5e7eb] bg-[#f8fafc] text-[#4b5563]'
}
*/

const getHistoryIcon = (call: SoftphoneCallSession): React.ReactElement => {
  if (isMissedHistoryCall(call)) {
    return <BsTelephoneXFill size={14} className="text-[#F03E3E]" />
  }

  const iconClass = isSuccessfulCallStatus(call.status)
    ? 'text-[#12a84f]'
    : 'text-[#F03E3E]'

  return getDirection(call) === 'outgoing' ? (
    <BsTelephoneOutboundFill size={14} className={iconClass} />
  ) : (
    <BsTelephoneInboundFill size={14} className={iconClass} />
  )
}

const formatTime = (dateValue?: string) => {
  if (!dateValue) return ''
  const date = new Date(dateValue)
  if (Number.isNaN(date.getTime())) return ''

  return date.toLocaleTimeString([], {
    hour: 'numeric',
    minute: '2-digit',
  })
}

const formatRelative = (dateValue?: string) => {
  if (!dateValue) return ''
  const date = new Date(dateValue)
  const diffMs = Date.now() - date.getTime()
  if (Number.isNaN(diffMs)) return ''

  const minutes = Math.max(0, Math.floor(diffMs / 60000))
  if (minutes < 1) return 'Just now'
  if (minutes < 60) return `${minutes}min${minutes === 1 ? '' : 's'} ago`
  const hours = Math.floor(minutes / 60)
  if (hours < 24) return `${hours} hour${hours === 1 ? '' : 's'} ago`
  const days = Math.floor(hours / 24)
  return `${days} day${days === 1 ? '' : 's'} ago`
}

const formatDuration = (seconds?: number) => {
  if (!seconds || seconds < 0) return '0:00'
  const totalSeconds = Math.floor(seconds)
  const minutes = Math.floor(totalSeconds / 60)
  const remainingSeconds = totalSeconds % 60

  if (minutes >= 60) {
    const hours = Math.floor(minutes / 60)
    const remainderMinutes = minutes % 60
    return `${hours}h ${remainderMinutes}min`
  }

  return `${minutes} min${minutes === 1 ? '' : 's'} ${remainingSeconds}sec`
}

const getCallDurationSeconds = (call: SoftphoneCallSession) => {
  const duration = call.duration_seconds ?? call.duration
  return typeof duration === 'number' && duration > 0
    ? Math.floor(duration)
    : undefined
}

const formatPlaybackTime = (seconds?: number) => {
  const totalSeconds = Math.max(0, Math.floor(seconds || 0))
  const minutes = Math.floor(totalSeconds / 60)
  const remainingSeconds = totalSeconds % 60

  return `${minutes.toString().padStart(2, '0')}:${remainingSeconds
    .toString()
    .padStart(2, '0')}`
}

const getHistoryDetail = (call: SoftphoneCallSession, startedAt?: string) => {
  const direction = getDirection(call) === 'incoming' ? 'Incoming' : 'Outgoing'

  const timestamp = [
    formatRelative(startedAt),
    startedAt ? formatTime(startedAt) : '',
  ]
    .filter(Boolean)
    .join(' • ')

  return [
    direction,
    timestamp,
    formatDuration(call.duration_seconds || call.duration),
  ]
    .filter(Boolean)
    .join(' • ')
}

const matchesHistoryDispatcherFilter = (
  call: SoftphoneCallSession,
  dispatcherId: HistoryDispatcherFilter
) => dispatcherId === 'all' || call.actor_id === dispatcherId

const getQueueAgentDispatcherIdCandidates = (agent: SoftphoneQueueAgent) => {
  const candidates = new Set<string>()
  const addCandidate = (value?: string) => {
    if (!value) return

    const normalized = value.trim()
    if (!normalized) return

    candidates.add(normalized)
    const uuidMatch = normalized.match(
      /([0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12})$/i
    )
    if (uuidMatch?.[1]) candidates.add(uuidMatch[1])
  }

  addCandidate(agent.user_id)
  addCandidate(agent.endpoint_id)
  addCandidate(getSipUserPart(agent.endpoint))
  addCandidate(getSipUserPart(agent.display_name))

  return Array.from(candidates)
}

const getQueueAgentLookupByDispatcherId = (agents?: SoftphoneQueueAgent[]) => {
  const lookup = new Map<string, SoftphoneQueueAgent>()

  agents?.forEach(agent => {
    getQueueAgentDispatcherIdCandidates(agent).forEach(id => {
      lookup.set(id, agent)
    })
  })

  return lookup
}

const getDispatcherPresenceStatus = (agent?: SoftphoneQueueAgent) => {
  if (!agent) return 'offline'

  const status = String(agent.status || '').toLowerCase()
  if (agent.paused) return 'paused'
  if (agent.in_call || ['in_use', 'busy', 'ringing'].includes(status)) {
    return 'engaged'
  }
  if (
    agent.available &&
    ['not_in_use', 'available', 'idle', 'ready'].includes(status)
  ) {
    return 'online'
  }
  if (agent.available) return 'online'

  return 'offline'
}

const matchesHistoryStatusFilter = (
  call: SoftphoneCallSession,
  statusFilter: HistoryCallStatusFilter
) => {
  if (statusFilter === 'all') return true

  const record = call as unknown as Record<string, unknown>
  const status = String(call.status || '').toLowerCase()
  const statusText = [status, record.outcome, record.disposition]
    .filter(Boolean)
    .join(' ')
    .toLowerCase()

  switch (statusFilter) {
    case 'missed':
      return isMissedHistoryCall(call)
    case 'completed':
      return status === 'complete' || isSuccessfulCallStatus(status)
    case 'declined':
      return (
        ['declined', 'rejected', 'cancelled', 'canceled'].includes(status) &&
        !isOutboundEndedCall(call)
      )
    case 'transferred':
      return statusText.includes('transfer')
    default:
      return true
  }
}

const startOfLocalDay = (date: Date) =>
  new Date(date.getFullYear(), date.getMonth(), date.getDate())

const addDays = (date: Date, days: number) => {
  const nextDate = new Date(date)
  nextDate.setDate(nextDate.getDate() + days)
  return nextDate
}

const parseDateInputBoundary = (value: string, endOfDay = false) => {
  if (!value) return undefined

  const [year, month, day] = value.split('-').map(Number)
  if (!year || !month || !day) return undefined

  const date = new Date(year, month - 1, day)
  if (Number.isNaN(date.getTime())) return undefined

  return endOfDay ? addDays(date, 1).getTime() : date.getTime()
}

const getHistoryTimeRange = (
  timeFilter: HistoryTimeFilter,
  customStartDate: string,
  customEndDate: string
) => {
  const now = new Date()
  const today = startOfLocalDay(now)
  const tomorrow = addDays(today, 1)

  switch (timeFilter) {
    case 'today':
      return { start: today.getTime(), end: tomorrow.getTime() }
    case 'yesterday': {
      const yesterday = addDays(today, -1)
      return { start: yesterday.getTime(), end: today.getTime() }
    }
    case 'last7Days':
      return { start: addDays(today, -6).getTime(), end: tomorrow.getTime() }
    case 'last2Weeks':
      return { start: addDays(today, -13).getTime(), end: tomorrow.getTime() }
    case 'thisMonth':
      return {
        start: new Date(today.getFullYear(), today.getMonth(), 1).getTime(),
        end: new Date(today.getFullYear(), today.getMonth() + 1, 1).getTime(),
      }
    case 'custom':
      return {
        start: parseDateInputBoundary(customStartDate),
        end: parseDateInputBoundary(customEndDate, true),
      }
    default:
      return { start: undefined, end: undefined }
  }
}

const matchesHistoryTimeFilter = (
  call: SoftphoneCallSession,
  timeFilter: HistoryTimeFilter,
  customStartDate: string,
  customEndDate: string
) => {
  if (timeFilter === 'all') return true

  const timestamp = getCallTime(call)
  if (!timestamp) return false

  const { start, end } = getHistoryTimeRange(
    timeFilter,
    customStartDate,
    customEndDate
  )

  return (
    (start === undefined || timestamp >= start) &&
    (end === undefined || timestamp < end)
  )
}

const recordingWaveBars = [
  8, 14, 11, 20, 15, 24, 12, 18, 26, 16, 10, 21, 13, 28, 18, 24, 12, 16, 22, 30,
  17, 11, 23, 15, 27, 20, 13, 18, 25, 12, 29, 16, 21, 14, 24, 19, 10, 22, 15,
  27, 18, 12, 24, 16, 28, 20, 13, 19, 23, 11, 26, 15, 21, 17, 29, 12,
]

const getCallDateTime = (call: SoftphoneCallSession) =>
  call.started_at ||
  call.created_at ||
  call.answered_at ||
  call.ended_at ||
  call.updated_at ||
  getOptionalStringField(call, 'createdAt') ||
  getOptionalStringField(call, 'updatedAt')

const getHistoryDateKey = (date: Date) =>
  `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(
    2,
    '0'
  )}-${String(date.getDate()).padStart(2, '0')}`

const getHistoryDateLabel = (dateValue?: string) => {
  if (!dateValue) return 'Unknown date'

  const date = new Date(dateValue)
  if (Number.isNaN(date.getTime())) return 'Unknown date'

  const day = startOfLocalDay(date).getTime()
  const today = startOfLocalDay(new Date()).getTime()
  const yesterday = addDays(startOfLocalDay(new Date()), -1).getTime()

  if (day === today) return 'Today'
  if (day === yesterday) return 'Yesterday'

  return date.toLocaleDateString([], {
    month: 'short',
    day: 'numeric',
    year:
      date.getFullYear() === new Date().getFullYear() ? undefined : 'numeric',
  })
}

const groupCallsByDate = (calls: SoftphoneCallSession[]) => {
  const groups = new Map<
    string,
    { key: string; label: string; calls: SoftphoneCallSession[] }
  >()

  calls.forEach(call => {
    const dateValue = getCallDateTime(call)
    const date = dateValue ? new Date(dateValue) : null
    const key =
      date && !Number.isNaN(date.getTime())
        ? getHistoryDateKey(date)
        : 'unknown'
    const existing = groups.get(key)

    if (existing) {
      existing.calls.push(call)
      return
    }

    groups.set(key, {
      key,
      label: getHistoryDateLabel(dateValue),
      calls: [call],
    })
  })

  return Array.from(groups.values())
}

const getFirstStringField = (source: unknown, fields: string[]) => {
  for (const field of fields) {
    const value = getOptionalStringField(source, field)
    if (value?.trim()) return value.trim()
  }

  return undefined
}

const getCallSearchText = (call: SoftphoneCallSession) => {
  const record = call as unknown as Record<string, unknown>

  return [
    call.status,
    record.outcome,
    record.disposition,
    record.last_event_type,
    record.lastEventType,
  ]
    .filter(Boolean)
    .join(' ')
    .toLowerCase()
}

const isTransferredHistoryCall = (call: SoftphoneCallSession) =>
  getCallSearchText(call).includes('transfer')

const getHistoryDetailTitle = (call: SoftphoneCallSession) => {
  if (isTransferredHistoryCall(call)) return 'Call Transferred'

  if (isUnsuccessfulCallStatus(call.status)) return 'Call Ended'

  const direction = getDirection(call)
  if (direction === 'incoming') return 'Incoming'
  if (direction === 'outgoing') return 'Outgoing'

  return getCallStatusLabel(call)
}

const getHistoryDetailMetadata = (
  call: SoftphoneCallSession,
  lookup: SoftphoneContactLookup,
  currentDispatcherId?: string
) => {
  const startedAt = getCallDateTime(call)
  const duration = getCallDurationSeconds(call)
  const transferredBy =
    getFirstStringField(call, [
      'transferred_by_name',
      'transfer_by_name',
      'transferred_by',
      'dispatcher_name',
      'actor_name',
    ]) || resolveCallResponderName(call, lookup)
  const answeringDispatcherId = getFirstStringField(call, [
    'winning_dispatcher_id',
    'answered_by_dispatcher_id',
    'assigned_to_actor_id',
    'dispatcher_id',
    'actor_id',
  ])
  const answeringDispatcherName =
    getFirstStringField(call, [
      'winning_dispatcher_name',
      'answered_by_dispatcher_name',
      'assigned_to_actor_name',
      'dispatcher_name',
      'actor_name',
    ]) ||
    (answeringDispatcherId
      ? lookup.byId.get(String(answeringDispatcherId))
      : undefined)
  const answeredByLabel =
    answeringDispatcherId &&
    currentDispatcherId &&
    String(answeringDispatcherId) === String(currentDispatcherId)
      ? 'Answered by You'
      : `Answered by ${answeringDispatcherName || 'Dispatcher'}`
  const actorLabel = isTransferredHistoryCall(call)
    ? `Transferred by ${transferredBy || 'Dispatcher'}`
    : isSuccessfulCallStatus(call.status) && getDirection(call) === 'incoming'
      ? answeredByLabel
      : isSuccessfulCallStatus(call.status) && getDirection(call) === 'outgoing'
        ? 'Answered'
        : getDirection(call) === 'outgoing' &&
            !isUnsuccessfulCallStatus(call.status)
          ? 'Called by You'
          : ''

  return [
    actorLabel,
    startedAt ? formatRelative(startedAt) : '',
    duration ? formatDuration(duration) : '',
  ]
    .filter(Boolean)
    .join(' • ')
}

const getHistoryCallRecordingUrls = (call: SoftphoneCallSession) => {
  const urls = new Set<string>()
  const addUrl = (value: unknown) => {
    if (typeof value === 'string' && value.trim()) urls.add(value.trim())
  }

  addUrl(call.recording_url)
  addUrl(call.recording_uri)
  call.recording_uris?.forEach(addUrl)
  call.recordings?.forEach(recording => {
    addUrl(recording.recording_uri)
    addUrl(recording.recording_url)
    addUrl(recording.uri)
    addUrl(recording.url)
  })

  return Array.from(urls)
}

const callMatchesHistoryDetailPhoneKeys = (
  call: SoftphoneCallSession,
  selectedPhoneKeys: Set<string>
) => {
  if (selectedPhoneKeys.size === 0) return false

  const callPhoneKeys = [
    call.assigned_to_phone_number,
    ...getCallPhoneCandidates(call),
  ].flatMap(value =>
    getPhoneLookupKeys(getPhoneCandidateDisplayValue(value) || value)
  )

  return callPhoneKeys.some(key => selectedPhoneKeys.has(key))
}

const matchesHistoryDetailCall = (
  call: SoftphoneCallSession,
  selectedCall: SoftphoneCallSession,
  selectedCorrelationTarget: string,
  selectedPhoneKeys: Set<string>,
  lookup: SoftphoneContactLookup
) => {
  if (call.call_id === selectedCall.call_id) return true
  if (callMatchesHistoryDetailPhoneKeys(call, selectedPhoneKeys)) return true

  return (
    Boolean(selectedCorrelationTarget) &&
    getCallCorrelationTarget(call, lookup) === selectedCorrelationTarget
  )
}

const getPresenceStatusDotClass = (status?: string) => {
  switch (String(status || '').toLowerCase()) {
    case 'offline':
    case 'unavailable':
      return 'bg-[#A0A0A0]'
    case 'paused':
    case 'engaged':
    case 'busy':
      return 'bg-[#F59E0B]'
    case 'online':
    case 'available':
    default:
      return 'bg-[#20B455]'
  }
}

const useCallTimer = (call: SoftphoneActiveCall | null) => {
  const [now, setNow] = useState(() => Date.now())

  React.useEffect(() => {
    if (!call?.answeredAt) return
    const interval = window.setInterval(() => setNow(Date.now()), 1000)
    return () => window.clearInterval(interval)
  }, [call?.answeredAt])

  if (!call?.answeredAt) return '00:00'
  const elapsed = Math.max(
    0,
    Math.floor((now - Date.parse(call.answeredAt)) / 1000)
  )
  const minutes = Math.floor(elapsed / 60)
  const seconds = elapsed % 60
  return `${minutes.toString().padStart(2, '0')}:${seconds
    .toString()
    .padStart(2, '0')}`
}

const EmptyState = ({
  title,
  subtitle,
  icon = <Phone size={48} className="text-[#969696]" />,
}: {
  title: string
  subtitle: string
  icon?: React.ReactNode
}) => (
  <div className="flex h-full flex-col items-center justify-center text-center">
    <div className="mb-5">{icon}</div>
    <p className="text-sm font-bold text-[#333333]">{title}</p>
    <p className="mt-2 text-sm text-off-black">{subtitle}</p>
  </div>
)

const SoftphoneTopTabs = () => {
  const { activeTab, setActiveTab } = useSoftphone()

  return (
    <div className="mx-4 mt-3 grid shrink-0 grid-cols-3 gap-2 rounded bg-[#F5F5F7] p-1">
      {topTabs.map(({ key, label, Icon }) => {
        const active = activeTab === key
        return (
          <button
            key={key}
            onClick={() => setActiveTab(key)}
            className={`flex h-8 items-center justify-center gap-2 rounded-lg text-xs transition-colors cursor-pointer ${
              active
                ? 'border border-primary bg-white font-semibold text-primary'
                : 'text-[#777777] hover:text-primary hover:bg-blue-100/50'
            }`}
            style={{
              color: active ? '#254769' : '#2C2C2C',
              fontWeight: active ? 500 : 400,
              fontSize: '14px',
            }}
          >
            <Icon size={15} />
            <span>{label}</span>
          </button>
        )
      })}
    </div>
  )
}

const SearchBox = ({
  value,
  onChange,
}: {
  value: string
  onChange: (value: string) => void
}) => (
  <div className="mx-4 mt-3 flex h-10 shrink-0 items-center gap-2 rounded-lg border border-[#939090] px-3">
    <Search size={16} className="text-[#939090] -mt-px" />
    <input
      value={value}
      onChange={event => onChange(event.target.value)}
      placeholder="Search..."
      className="h-full min-w-0 flex-1 bg-transparent outline-none"
      style={{ fontSize: '14px' }}
    />
  </div>
)

function HistoryFilterDropdown<TValue extends string>({
  placeholder,
  value,
  options,
  onChange,
  align = 'left',
  disabled = false,
  keepOpenOnSelect,
  children,
}: {
  placeholder: string
  value: TValue
  options: Array<HistoryDropdownOption<TValue>>
  onChange: (value: TValue) => void
  align?: 'left' | 'right'
  disabled?: boolean
  keepOpenOnSelect?: (value: TValue) => boolean
  children?: React.ReactNode
}) {
  const [isOpen, setIsOpen] = useState(false)
  const wrapperRef = useRef<HTMLDivElement>(null)
  const selectedOption = options.find(option => option.value === value)
  const buttonLabel = value === 'all' ? placeholder : selectedOption?.label

  React.useEffect(() => {
    if (!isOpen) return

    const handlePointerDown = (event: MouseEvent) => {
      if (
        wrapperRef.current &&
        !wrapperRef.current.contains(event.target as Node)
      ) {
        setIsOpen(false)
      }
    }
    const handleKeyDown = (event: KeyboardEvent) => {
      if (event.key === 'Escape') setIsOpen(false)
    }

    document.addEventListener('mousedown', handlePointerDown)
    document.addEventListener('keydown', handleKeyDown)

    return () => {
      document.removeEventListener('mousedown', handlePointerDown)
      document.removeEventListener('keydown', handleKeyDown)
    }
  }, [isOpen])

  return (
    <div ref={wrapperRef} className="relative min-w-0">
      <button
        type="button"
        onClick={() => setIsOpen(current => !current)}
        disabled={disabled}
        className={`flex h-7 w-full min-w-0 items-center font-medium justify-between gap-2 rounded border-[0.4px] bg-white px-2 text-left text-xs transition-colors ${
          isOpen
            ? 'border-primary text-primary'
            : 'border-[#CDD0D5] text-dark-gray-30 hover:border-primary'
        } ${disabled ? 'cursor-not-allowed opacity-60' : 'cursor-pointer'}`}
        style={{ fontSize: '12px', fontWeight: 500, color: '#545252' }}
        aria-haspopup="listbox"
        aria-expanded={isOpen}
      >
        <span className="truncate">{buttonLabel || placeholder}</span>
        <ChevronDown
          size={14}
          className={`shrink-0 transition-transform ${
            isOpen ? 'rotate-180' : ''
          }`}
        />
      </button>

      {isOpen ? (
        <div
          role="listbox"
          className={`absolute top-full z-50 mt-2 max-h-72 min-w-[190px] overflow-y-auto rounded-md border border-[#E6E2E1] bg-white py-1 shadow-[0_20px_50px_rgba(0,0,0,0.22)] ${
            align === 'right' ? 'right-0' : 'left-0'
          }`}
        >
          {options.map(option => {
            const selected = option.value === value
            return (
              <div
                key={option.value}
                // type="button"
                role="option"
                aria-selected={selected}
                onClick={() => {
                  onChange(option.value)
                  if (!keepOpenOnSelect?.(option.value)) {
                    setIsOpen(false)
                  }
                }}
                className={`flex w-full items-center justify-between gap-3 px-4 py-2 text-left text-sm transition-colors cursor-pointer ${
                  selected
                    ? 'bg-[#F5F5F7] font-medium text-primary'
                    : 'text-[#2A2525] hover:bg-[#F5F5F7]'
                }`}
                style={{ fontSize: '12px' }}
              >
                <span className="min-w-0 truncate capitalize">
                  {option.label}
                </span>
                {option.status ? (
                  <span
                    className={`h-1.5 w-1.5 shrink-0 rounded-full ${getPresenceStatusDotClass(
                      option.status
                    )}`}
                    aria-hidden="true"
                  />
                ) : null}
              </div>
            )
          })}
          {children ? (
            <div className="border-t border-[#E6E2E1] px-4 py-3">
              {children}
            </div>
          ) : null}
        </div>
      ) : null}
    </div>
  )
}

const RecordingPlayer = ({
  url,
  durationSeconds,
}: {
  url: string
  durationSeconds?: number
}) => {
  const audioRef = useRef<HTMLAudioElement>(null)
  const [isPlaying, setIsPlaying] = useState(false)
  const [currentTime, setCurrentTime] = useState(0)
  const [duration, setDuration] = useState(durationSeconds || 0)
  const [error, setError] = useState('')
  const effectiveDuration = duration || durationSeconds || 0
  const progress =
    effectiveDuration > 0 ? Math.min(currentTime / effectiveDuration, 1) : 0
  const activeBars = Math.round(progress * recordingWaveBars.length)

  React.useEffect(() => {
    setIsPlaying(false)
    setCurrentTime(0)
    setDuration(durationSeconds || 0)
    setError('')
  }, [durationSeconds, url])

  React.useEffect(() => {
    const audio = audioRef.current
    return () => {
      audio?.pause()
    }
  }, [])

  const handleTogglePlayback = async () => {
    const audio = audioRef.current
    if (!audio) return

    if (isPlaying) {
      audio.pause()
      setIsPlaying(false)
      return
    }

    try {
      setError('')
      await audio.play()
      setIsPlaying(true)
    } catch {
      setIsPlaying(false)
      setError('Recording unavailable')
    }
  }

  const handleSeek = (event: React.MouseEvent<HTMLButtonElement>) => {
    const audio = audioRef.current
    if (!audio || effectiveDuration <= 0) return

    const rect = event.currentTarget.getBoundingClientRect()
    const ratio = Math.min(
      Math.max((event.clientX - rect.left) / rect.width, 0),
      1
    )
    const nextTime = ratio * effectiveDuration
    audio.currentTime = nextTime
    setCurrentTime(nextTime)
  }

  return (
    <div className="mt-2">
      <audio
        ref={audioRef}
        src={url}
        preload="metadata"
        onLoadedMetadata={event => {
          const nextDuration = event.currentTarget.duration
          if (Number.isFinite(nextDuration)) {
            setDuration(Math.floor(nextDuration))
          }
        }}
        onPause={() => setIsPlaying(false)}
        onPlay={() => setIsPlaying(true)}
        onEnded={() => {
          setIsPlaying(false)
          setCurrentTime(0)
        }}
        onError={() => {
          setIsPlaying(false)
          setError('Recording unavailable')
        }}
        onTimeUpdate={event => setCurrentTime(event.currentTarget.currentTime)}
      />
      <div className="flex h-8 items-center gap-2">
        <button
          type="button"
          onClick={handleTogglePlayback}
          className="flex h-6 w-fit shrink-0 items-center justify-center rounded-full text-[#313030] transition-colors cursor-pointer"
          aria-label={isPlaying ? 'Pause recording' : 'Play recording'}
          title={isPlaying ? 'Pause recording' : 'Play recording'}
        >
          {isPlaying ? (
            <Pause size={13} className="hover:text-[#939090]" />
          ) : (
            <Play size={13} className="hover:text-[#939090]" />
          )}
        </button>
        <button
          type="button"
          onClick={handleSeek}
          disabled={effectiveDuration <= 0}
          className="flex h-7 min-w-0 flex-1 items-center gap-px overflow-hidden disabled:cursor-default"
          aria-label="Seek call recording"
          title="Seek call recording"
        >
          {recordingWaveBars.map((height, index) => (
            <span
              key={`${url}-${index}`}
              className={`w-0.5 shrink-0 rounded-full ${
                index <= activeBars ? 'bg-[#5A6472]' : 'bg-[#C9CDD3]'
              }`}
              style={{ height: `${height}px` }}
              aria-hidden="true"
            />
          ))}
        </button>
        <span className="w-11 shrink-0 text-right text-xs font-medium text-[#545252]">
          {formatPlaybackTime(effectiveDuration || currentTime)}
        </span>
      </div>
      {error ? (
        <div className="mt-1 text-xs text-[#d83a4a]">{error}</div>
      ) : null}
    </div>
  )
}

const HistoryDetailCallItem = ({
  call,
  contactLookup,
  currentDispatcherId,
}: {
  call: SoftphoneCallSession
  contactLookup: SoftphoneContactLookup
  currentDispatcherId?: string
}) => {
  const startedAt = getCallDateTime(call)
  const metadata = getHistoryDetailMetadata(
    call,
    contactLookup,
    currentDispatcherId
  )
  const recordingUrls = getHistoryCallRecordingUrls(call)

  return (
    <div className="grid grid-cols-[24px_minmax(0,1fr)] gap-2 px-3 py-2">
      <div className="mt-1">{getHistoryIcon(call)}</div>
      <div className="min-w-0">
        <div className="flex items-start justify-between gap-3">
          <div className="min-w-0 truncate text-sm font-semibold text-[#313030]">
            {getHistoryDetailTitle(call)}
          </div>
          <span className="shrink-0 text-xs font-medium text-[#1A1B23]">
            {formatTime(startedAt)}
          </span>
        </div>
        {metadata ? (
          <div className="mt-1 text-xs leading-[140%] text-[#1A1B23]">
            {metadata}
          </div>
        ) : null}
        {recordingUrls.map((recordingUrl, index) => (
          <RecordingPlayer
            key={`${call.call_id}-recording-${index}`}
            url={recordingUrl}
            durationSeconds={getCallDurationSeconds(call)}
          />
        ))}
      </div>
    </div>
  )
}

const HistoryDetailsView = ({
  selectedCall,
  baseCalls,
  contactLookup,
  onBack,
}: {
  selectedCall: SoftphoneCallSession
  baseCalls: SoftphoneCallSession[]
  contactLookup: SoftphoneContactLookup
  onBack: () => void
}) => {
  const { dispatcher } = useDispatcher()
  const { isEligible, isOpen } = useSoftphone()
  const selectedPhoneNumber = useMemo(
    () => getCallableHistoryNumber(selectedCall),
    [selectedCall]
  )
  const selectedPhoneKeys = useMemo(
    () => new Set(getPhoneLookupKeys(selectedPhoneNumber)),
    [selectedPhoneNumber]
  )
  const selectedCorrelationTarget = useMemo(
    () => getCallCorrelationTarget(selectedCall, contactLookup),
    [contactLookup, selectedCall]
  )
  const { data, isLoading } = useSoftphoneCallHistory(
    {
      actor_id: dispatcher?.dispatcher_id,
      phone_number: selectedPhoneNumber || undefined,
      limit: 100,
    },
    isOpen && isEligible && Boolean(selectedPhoneNumber)
  )
  const detailCalls = useMemo(() => {
    const merged = new Map<string, SoftphoneCallSession>()
    const candidates = [selectedCall, ...baseCalls, ...(data?.calls || [])]

    candidates.forEach(call => {
      if (
        !matchesHistoryDetailCall(
          call,
          selectedCall,
          selectedCorrelationTarget,
          selectedPhoneKeys,
          contactLookup
        )
      ) {
        return
      }

      const existing = merged.get(call.call_id)
      if (!existing || shouldReplaceHistoryCall(existing, call)) {
        merged.set(call.call_id, call)
      }
    })

    return Array.from(merged.values()).sort(
      (a, b) => getCallTime(b) - getCallTime(a)
    )
  }, [
    baseCalls,
    contactLookup,
    data?.calls,
    selectedCall,
    selectedCorrelationTarget,
    selectedPhoneKeys,
  ])
  const groupedCalls = useMemo(
    () => groupCallsByDate(detailCalls),
    [detailCalls]
  )
  const displayNumber = selectedPhoneNumber || getDisplayTarget(selectedCall)
  const contactName = resolveCallContactName(selectedCall, contactLookup)
  const subtitle =
    contactName && contactName !== displayNumber ? contactName : 'Unknown'

  return (
    <div className="flex min-h-0 flex-1 flex-col">
      <div className="flex shrink-0 items-center gap-2 px-2 py-3">
        <button
          type="button"
          onClick={onBack}
          className="flex h-8 w-8 shrink-0 items-center justify-center rounded-full text-[#777777] transition-colors hover:bg-[#F5F5F7] hover:text-[#313030] cursor-pointer"
          aria-label="Back to call history"
          title="Back to call history"
          style={{ marginTop: '-1px' }}
        >
          <ArrowLeft size={18} className="text-[#939090]" />
        </button>
        <div className="flex min-w-0 items-center gap-2">
          <div className="min-w-0 truncate text-sm font-semibold text-[#313030]">
            {displayNumber}
          </div>
          <span className="min-w-0 truncate text-sm font-medium text-[#545252] capitalize">
            {subtitle}
          </span>
        </div>
      </div>

      <div className="min-h-0 flex-1 overflow-y-auto px-4 pb-4">
        {isLoading && detailCalls.length <= 1 ? (
          <div className="flex h-full items-center justify-center">
            <LoaderCircle size={28} className="animate-spin text-primary" />
          </div>
        ) : groupedCalls.length === 0 ? (
          <EmptyState
            title="No call details"
            subtitle="Call activity for this number will appear here"
          />
        ) : (
          groupedCalls.map(group => (
            <section key={group.key} className="mb-3">
              <div className="mb-2 text-sm text-[#939090]">{group.label}</div>
              <div className="overflow-hidden rounded border-[0.4px] border-[#E6E2E1] bg-white">
                <div className="divide-y-[0.4px] divide-dashed divide-[#E6E2E1]">
                  {group.calls.map(call => (
                    <HistoryDetailCallItem
                      key={call.call_id}
                      call={call}
                      contactLookup={contactLookup}
                      currentDispatcherId={dispatcher?.dispatcher_id}
                    />
                  ))}
                </div>
                <div className="flex items-center gap-2 border-t-[0.4px] border-dashed border-[#E6E2E1] px-3 py-2 text-xs text-[#1A1B23]">
                  <span className="rounded bg-[#E9F1FF] px-1.5 py-0.5 font-medium">
                    {getCallCategory(group.calls[0])}
                  </span>
                  <span aria-hidden="true">•</span>
                  <span className="text-[#1A1B23] font-medium">
                    {getCallStatusLabel(group.calls[0])}
                  </span>
                </div>
              </div>
            </section>
          ))
        )}
      </div>
    </div>
  )
}

const HistoryView = ({
  selectedCall,
  onSelectCall,
  onBackFromDetails,
}: {
  selectedCall: SoftphoneCallSession | null
  onSelectCall: (call: SoftphoneCallSession) => void
  onBackFromDetails: () => void
}) => {
  const { dispatcher } = useDispatcher()
  const { attemptedCalls, isOpen, isEligible, startDialpadCall } =
    useSoftphone()
  const [search, setSearch] = useState('')
  const [filter, setFilter] = useState<HistoryFilter>('all')
  const [statusFilter, setStatusFilter] =
    useState<HistoryCallStatusFilter>('all')
  const [timeFilter, setTimeFilter] = useState<HistoryTimeFilter>('all')
  const [dispatcherFilter, setDispatcherFilter] =
    useState<HistoryDispatcherFilter>('all')
  const [customStartDate, setCustomStartDate] = useState('')
  const [customEndDate, setCustomEndDate] = useState('')
  const currentPrecinctId = dispatcher?.precinct_id
  const { data, isLoading } = useSoftphoneCallHistory(
    { actor_id: dispatcher?.dispatcher_id, limit: 50 },
    isOpen && Boolean(dispatcher?.dispatcher_id)
  )
  const { data: dispatchersData, isLoading: isLoadingDispatchers } =
    useGetAllDispatchers()
  const { data: queueStatus } = useSoftphoneQueueStatus(
    dispatcher?.dispatcher_id,
    isOpen && isEligible
  )
  const patientPhoneNumbers = useMemo(
    () =>
      [...attemptedCalls, ...(data?.calls || [])].flatMap(
        getCallPhoneCandidates
      ),
    [attemptedCalls, data?.calls]
  )
  const contactLookup = useSoftphoneContactLookup(
    patientPhoneNumbers,
    isOpen && isEligible
  )

  const queueAgentByDispatcherId = useMemo(() => {
    return getQueueAgentLookupByDispatcherId(queueStatus?.agents)
  }, [queueStatus?.agents])

  const dispatcherOptions = useMemo<
    Array<HistoryDropdownOption<HistoryDispatcherFilter>>
  >(() => {
    const options = new Map<
      string,
      HistoryDropdownOption<HistoryDispatcherFilter>
    >()

    getDispatchersFromResponse(dispatchersData)
      .filter(dispatcherItem =>
        matchesCurrentPrecinct(dispatcherItem, currentPrecinctId)
      )
      .forEach(dispatcherItem => {
        const dispatcherId = dispatcherItem.dispatcher_id
        if (!dispatcherId) return

        options.set(dispatcherId, {
          value: dispatcherId,
          label: getDispatcherDisplayName(dispatcherItem),
          status: getDispatcherPresenceStatus(
            queueAgentByDispatcherId.get(dispatcherId)
          ),
        })
      })

    return [
      { value: 'all', label: 'All dispatchers' },
      ...Array.from(options.values()).sort((a, b) =>
        a.label.localeCompare(b.label)
      ),
    ]
  }, [currentPrecinctId, dispatchersData, queueAgentByDispatcherId])

  const dispatcherNameById = useMemo(
    () =>
      new Map(
        dispatcherOptions
          .filter(option => option.value !== 'all')
          .map(option => [option.value, option.label])
      ),
    [dispatcherOptions]
  )

  const baseCalls = useMemo(() => {
    const backendCalls = data?.calls || []
    const backendCallIds = new Set(backendCalls.map(call => call.call_id))
    const hasMatchingBackendInboundCall = (localCall: SoftphoneCallSession) => {
      if (
        getDirection(localCall) !== 'incoming' ||
        !String(localCall.call_id).startsWith('local-inbound-')
      ) {
        return false
      }

      const localPhones = getCallPhoneCandidates(localCall)
        .map(normalizePhoneForLookup)
        .filter(Boolean)
      const localTime = getCallTime(localCall)

      return backendCalls.some(backendCall => {
        if (getDirection(backendCall) !== 'incoming') return false
        if (Math.abs(getCallTime(backendCall) - localTime) > 5 * 60 * 1000) {
          return false
        }

        return getCallPhoneCandidates(backendCall)
          .map(normalizePhoneForLookup)
          .filter(Boolean)
          .some(backendPhone =>
            localPhones.some(
              localPhone =>
                backendPhone.endsWith(localPhone) ||
                localPhone.endsWith(backendPhone)
            )
          )
      })
    }

    return dedupeHistoryCalls(
      [
        ...attemptedCalls.filter(
          call =>
            !backendCallIds.has(call.call_id) &&
            !hasMatchingBackendInboundCall(call)
        ),
        ...backendCalls,
      ],
      contactLookup
    ).sort((a, b) => getCallTime(b) - getCallTime(a))
  }, [attemptedCalls, contactLookup, data?.calls])

  const calls = useMemo(() => {
    const query = search.trim().toLowerCase()

    return baseCalls.filter(call => {
      const direction = getDirection(call)
      const matchesFilter =
        filter === 'all' ||
        (filter === 'missed' && isMissedHistoryCall(call)) ||
        direction === filter
      const matchesStatus = matchesHistoryStatusFilter(call, statusFilter)
      const matchesTime = matchesHistoryTimeFilter(
        call,
        timeFilter,
        customStartDate,
        customEndDate
      )
      const matchesDispatcher = matchesHistoryDispatcherFilter(
        call,
        dispatcherFilter
      )
      const searchable = [
        resolveCallContactName(call, contactLookup),
        resolveCallResponderName(call, contactLookup),
        call.actor_id ? dispatcherNameById.get(call.actor_id) : undefined,
        getDisplayTarget(call),
        getCallCategory(call),
        getCallStatusLabel(call),
        call.actor_id,
        call.from,
        call.to,
        call.caller_id,
        call.dial_target,
        call.assigned_to_phone_number,
      ]
        .filter(Boolean)
        .join(' ')
        .toLowerCase()
      const matchesSearch = !query || searchable.includes(query)

      return (
        matchesFilter &&
        matchesStatus &&
        matchesTime &&
        matchesDispatcher &&
        matchesSearch
      )
    })
  }, [
    baseCalls,
    contactLookup,
    customEndDate,
    customStartDate,
    dispatcherFilter,
    dispatcherNameById,
    filter,
    search,
    statusFilter,
    timeFilter,
  ])

  const hasActiveCriteria =
    Boolean(search.trim()) ||
    filter !== 'all' ||
    statusFilter !== 'all' ||
    timeFilter !== 'all' ||
    dispatcherFilter !== 'all'
  const emptyTitle =
    hasActiveCriteria && baseCalls.length > 0
      ? 'No matching calls'
      : 'No call history'
  const emptySubtitle =
    hasActiveCriteria && baseCalls.length > 0
      ? 'Try adjusting search or filters'
      : 'Call logs will appear here'

  if (selectedCall) {
    return (
      <HistoryDetailsView
        selectedCall={selectedCall}
        baseCalls={baseCalls}
        contactLookup={contactLookup}
        onBack={onBackFromDetails}
      />
    )
  }

  return (
    <div className="font-family-sans flex min-h-0 flex-1 flex-col">
      <SearchBox value={search} onChange={setSearch} />

      <div className="mx-4 mt-3 grid shrink-0 grid-cols-3 gap-2">
        <HistoryFilterDropdown
          placeholder="Call Status"
          value={statusFilter}
          options={historyStatusFilterOptions}
          onChange={setStatusFilter}
        />
        <HistoryFilterDropdown
          placeholder="Time Stamp"
          value={timeFilter}
          options={historyTimeFilterOptions}
          onChange={setTimeFilter}
          keepOpenOnSelect={value => value === 'custom'}
        >
          {timeFilter === 'custom' ? (
            <div className="space-y-2 text-xs text-[#4B4B4B]">
              <label className="block">
                <span className="mb-1 block">From</span>
                <input
                  type="date"
                  value={customStartDate}
                  max={customEndDate || undefined}
                  onChange={event => setCustomStartDate(event.target.value)}
                  className="h-8 w-full rounded border border-[#E6E2E1] px-2 outline-none focus:border-primary"
                />
              </label>
              <label className="block">
                <span className="mb-1 block">To</span>
                <input
                  type="date"
                  value={customEndDate}
                  min={customStartDate || undefined}
                  onChange={event => setCustomEndDate(event.target.value)}
                  className="h-8 w-full rounded border border-[#E6E2E1] px-2 outline-none focus:border-primary"
                />
              </label>
            </div>
          ) : null}
        </HistoryFilterDropdown>
        <HistoryFilterDropdown
          placeholder="Dispatcher"
          value={dispatcherFilter}
          options={dispatcherOptions}
          onChange={setDispatcherFilter}
          align="right"
          disabled={isLoadingDispatchers && dispatcherOptions.length <= 1}
        />
      </div>

      {/* History Tabs */}
      <div className="mx-4 mt-2 grid shrink-0 grid-cols-4 bg-[#fbfbfb]">
        {historyFilters.map(item => (
          <button
            key={item.key}
            onClick={() => setFilter(item.key)}
            className={`h-10 border-b text-xs transition-colors cursor-pointer ${
              filter === item.key
                ? item.key === 'missed'
                  ? 'border-[#FA5252] text-[#FA5252]'
                  : 'border-primary font-medium text-primary'
                : 'border-transparent text-[#777777] hover:text-primary hover:bg-blue-100/50'
            }`}
            style={{
              fontSize: '12px',
              fontWeight: filter === item.key ? 500 : 400,
              color:
                filter === item.key
                  ? item.key === 'missed'
                    ? '#FA5252'
                    : '#254769'
                  : item.key === 'missed'
                    ? '#FA5252'
                    : '#797676',
            }}
          >
            {item.label}
          </button>
        ))}
      </div>

      <div className="min-h-0 flex-1 overflow-y-auto pl-4 pr-2.5 py-3">
        {isLoading ? (
          <div className="flex h-full items-center justify-center">
            <LoaderCircle size={28} className="animate-spin text-primary" />
          </div>
        ) : calls.length === 0 ? (
          <EmptyState title={emptyTitle} subtitle={emptySubtitle} />
        ) : (
          <div>
            {calls.map(call => {
              const target = resolveCallContactName(call, contactLookup)
              // const responderName = resolveCallResponderName(
              //   call,
              //   contactLookup
              // )
              const callableNumber = getCallableHistoryNumber(call)
              const primaryLabel = callableNumber || getDisplayTarget(call)
              const secondaryLabel = primaryLabel !== target ? target : ''
              const startedAt = call.started_at || call.created_at
              return (
                <React.Fragment key={call.call_id}>
                  <div
                    role="button"
                    tabIndex={0}
                    onClick={() => onSelectCall(call)}
                    onKeyDown={event => {
                      if (event.key === 'Enter' || event.key === ' ') {
                        event.preventDefault()
                        onSelectCall(call)
                      }
                    }}
                    className="grid grid-cols-[28px_minmax(0,1fr)_96px] items-start gap-0.5 p-2 rounded border-[0.4px] border-[#E6E2E1] mb-2 transition-colors hover:border-primary/50 hover:bg-[#F8FAFD] focus:outline-none focus-visible:ring-2 focus-visible:ring-primary cursor-pointer"
                    aria-label={`View call details for ${target}`}
                  >
                    <div className="mt-1.5 ml-1">{getHistoryIcon(call)}</div>
                    <div className="min-w-0">
                      <div className="flex items-center truncate capitalize">
                        <span className="font-semibold text-[#313030] text-sm">
                          {primaryLabel}&nbsp;
                        </span>

                        {secondaryLabel ? (
                          <span className="text-[#1A1B23] text-xs">
                            {callableNumber && '• '}
                            {secondaryLabel}
                          </span>
                        ) : null}
                      </div>
                      <div className="mt-1 truncate text-xs text-[#20232a]">
                        {getHistoryDetail(call, startedAt)}
                      </div>
                      {/* {responderName ? (
                        <div className="mt-1 truncate text-xs font-medium text-[#0d8f45]">
                          {responderName}
                        </div>
                      ) : null} */}
                    </div>
                    <div className="flex min-w-0 items-center justify-end gap-2">
                      {callableNumber ? (
                        <button
                          type="button"
                          onClick={event => {
                            event.stopPropagation()
                            void startDialpadCall(callableNumber)
                          }}
                          className="flex h-6 w-fit px-2 shrink-0 gap-1 items-center justify-center rounded bg-[#E9F1FF] transition-colors hover:bg-[#e1e9f6] cursor-pointer"
                          aria-label={`Call ${target}`}
                          title={`Call ${target}`}
                        >
                          <Phone size={12} className="text-primary" />
                          <span className="text-[#1A1B23] text-xs font-medium">
                            Call
                          </span>
                        </button>
                      ) : null}
                      {/* <div className="flex min-w-0 flex-col items-end gap-1 text-right">
                        <span
                          className={`max-w-full truncate rounded border px-2 py-0.5 text-[10px] font-semibold leading-4 ${getCallCategoryClasses(call)}`}
                          title={getCallCategory(call)}
                        >
                          {getCallCategory(call)}
                        </span>
                        <span className="max-w-full truncate text-[10px] capitalize leading-4 text-[#6b7280]">
                          {getCallStatusLabel(call)}
                        </span>
                      </div> */}
                    </div>
                  </div>
                </React.Fragment>
              )
            })}
          </div>
        )}
      </div>
    </div>
  )
}

const DialpadView = () => {
  const {
    dialpadNumber,
    appendDialpadDigit,
    pasteDialpadNumber,
    deleteDialpadDigit,
    clearDialpadNumber,
    startDialpadCall,
    registrationStatus,
    isEligible,
  } = useSoftphone()
  const debouncedDialpadNumber = useDebounce(dialpadNumber, 300)
  const contactLookup = useSoftphoneContactLookup(
    [debouncedDialpadNumber],
    isEligible && normalizePhoneForLookup(debouncedDialpadNumber).length >= 7
  )
  const longPressTimeoutRef = useRef<number | null>(null)
  const longPressTriggeredRef = useRef(false)
  const dialpadContactName = useMemo(
    () => resolvePhoneContactName(dialpadNumber, contactLookup),
    [contactLookup, dialpadNumber]
  )

  const clearLongPressTimer = () => {
    if (longPressTimeoutRef.current === null) return

    window.clearTimeout(longPressTimeoutRef.current)
    longPressTimeoutRef.current = null
  }

  React.useEffect(() => {
    return () => {
      if (longPressTimeoutRef.current !== null) {
        window.clearTimeout(longPressTimeoutRef.current)
      }
    }
  }, [])

  const handleKeyPointerDown = (digit: string) => {
    if (digit !== '0') return

    longPressTriggeredRef.current = false
    clearLongPressTimer()
    longPressTimeoutRef.current = window.setTimeout(() => {
      longPressTimeoutRef.current = null
      longPressTriggeredRef.current = true
      appendDialpadDigit('+')
    }, 500)
  }

  const handleKeyPointerEnd = () => {
    clearLongPressTimer()
  }

  const handleKeyClick = (digit: string) => {
    if (digit === '0' && longPressTriggeredRef.current) {
      longPressTriggeredRef.current = false
      return
    }

    appendDialpadDigit(digit)
  }

  const canCallDialpadNumber =
    Boolean(dialpadNumber) && isEligible && registrationStatus === 'registered'

  const handleDialpadCall = () => {
    if (!canCallDialpadNumber) return

    void startDialpadCall(dialpadNumber)
  }

  React.useEffect(() => {
    const handleKeyboardInput = (event: KeyboardEvent) => {
      const target = event.target as HTMLElement | null
      const targetTag = target?.tagName?.toLowerCase()
      const isEditableTarget =
        target?.isContentEditable ||
        targetTag === 'input' ||
        targetTag === 'textarea' ||
        targetTag === 'select'

      if (isEditableTarget || event.ctrlKey || event.metaKey || event.altKey) {
        return
      }

      if (/^[0-9]$/.test(event.key) || event.key === '*' || event.key === '#') {
        event.preventDefault()
        appendDialpadDigit(event.key)
        return
      }

      if (event.key === '+') {
        event.preventDefault()
        appendDialpadDigit('+')
        return
      }

      if (event.key === 'Backspace' || event.key === 'Delete') {
        event.preventDefault()
        deleteDialpadDigit()
        return
      }

      if (event.key === 'Enter') {
        event.preventDefault()
        handleDialpadCall()
      }
    }

    window.addEventListener('keydown', handleKeyboardInput)
    return () => window.removeEventListener('keydown', handleKeyboardInput)
  }, [appendDialpadDigit, deleteDialpadDigit, handleDialpadCall])

  const handleDialpadPaste = (event: React.ClipboardEvent) => {
    event.preventDefault()
    pasteDialpadNumber(event.clipboardData.getData('text'))
  }

  const handleClipboardPaste = async () => {
    try {
      const clipboardText = await navigator.clipboard?.readText()
      pasteDialpadNumber(clipboardText || '')
    } catch {
      // Browser clipboard permissions can block button-driven reads; keyboard paste still works.
    }
  }

  return (
    <form
      onSubmit={event => event.preventDefault()}
      onPaste={handleDialpadPaste}
      className="min-h-0 flex-1 overflow-y-auto px-4 pb-4"
    >
      <div className="flex min-h-full flex-col justify-end">
        <div className="mb-2 flex min-h-24 flex-col items-center justify-center">
          <div
            tabIndex={0}
            role="textbox"
            aria-label="Dialpad number"
            aria-readonly="true"
            className={`flex min-h-16 max-w-full items-center justify-center break-all bg-white text-center text-[42px] font-medium leading-tight ${
              dialpadNumber ? 'text-[#4a4a4a]' : 'text-[#969696]'
            }`}
          >
            {formatDialpadNumber(dialpadNumber)}
          </div>
          <div className="mt-1 h-5 max-w-full truncate text-sm font-medium text-primary">
            {dialpadNumber && dialpadContactName ? dialpadContactName : ''}
          </div>
        </div>

        <div className="mb-8 flex items-center justify-between">
          <button
            type="button"
            onClick={handleClipboardPaste}
            className="flex h-10 w-10 items-center justify-center rounded-full text-[#6c6c6c] hover:bg-gray-100 cursor-pointer"
            aria-label="Paste number"
            title="Paste number"
          >
            <ClipboardPaste size={21} />
          </button>
          <button
            type="button"
            onClick={dialpadNumber ? deleteDialpadDigit : clearDialpadNumber}
            className="flex h-10 w-10 items-center justify-center rounded-full text-[#6c6c6c] hover:bg-gray-100 cursor-pointer"
            aria-label="Delete digit"
          >
            <Delete size={22} />
          </button>
        </div>

        <div className="mx-auto mb-4 grid w-full grid-cols-3 gap-2">
          {dialpadKeys.map(key => (
            <button
              type="button"
              key={key.digit}
              onClick={() => handleKeyClick(key.digit)}
              onContextMenu={event => {
                if (key.digit === '0') event.preventDefault()
              }}
              onPointerCancel={handleKeyPointerEnd}
              onPointerDown={() => handleKeyPointerDown(key.digit)}
              onPointerLeave={handleKeyPointerEnd}
              onPointerUp={handleKeyPointerEnd}
              className="flex h-24 flex-col items-center justify-center rounded-lg border border-[#e2e2e2] bg-white text-[#2a2a2a] transition-colors hover:bg-[#f8fafc] cursor-pointer"
              aria-label={
                key.digit === '0' ? '0, press and hold for plus' : key.digit
              }
            >
              <span className="text-2xl font-bold">{key.digit}</span>
              {!['*', '#'].includes(key.digit) && (
                <span
                  className={`h-4 text-xs text-[#8b8b8b] ${
                    key.letters ? '' : 'invisible'
                  }`}
                >
                  {key.letters || '-'}
                </span>
              )}
            </button>
          ))}
        </div>

        <button
          type="button"
          disabled={!canCallDialpadNumber}
          onClick={handleDialpadCall}
          title="Call number"
          className="flex h-[60px] items-center justify-center gap-2 rounded border border-dashed border-primary bg-[#eaf2ff] text-sm font-semibold text-primary disabled:cursor-not-allowed disabled:opacity-70 cursor-pointer"
        >
          <Phone size={20} />
          Call
        </button>
      </div>
    </form>
  )
}

const ContactsView = () => {
  const [search, setSearch] = useState('')
  const [selectedTab, setSelectedTab] = useState<ContactTab>('all')
  const { startDialpadCall, startResponderCall } = useSoftphone()
  const { dispatcher } = useDispatcher()
  const currentPrecinctId = dispatcher?.precinct_id
  const { data: respondersData, isLoading: loadingResponders } =
    useAllResponders({
      pageNumber: 1,
      numberPerPage: 100,
      precinct_id: currentPrecinctId,
    })
  const { data: hospitals = [], isLoading: loadingHospitals } = useAllHospitals(
    {
      pageNumber: 1,
      numberPerPage: 100,
      precinct_id: currentPrecinctId,
    }
  )
  const { data: ambulanceData, isLoading: loadingAmbulances } =
    useAmbulanceProviders({ limit: 100, precinct_id: currentPrecinctId })

  const contacts = useMemo<SoftphoneContact[]>(() => {
    const responderContacts =
      respondersData?.items
        ?.filter(responder =>
          matchesCurrentPrecinct(responder, currentPrecinctId)
        )
        .map(responder => ({
          id: responder.responder_id,
          name:
            [responder.first_name, responder.last_name]
              .filter(Boolean)
              .join(' ') ||
            responder.phone_number ||
            'Responder',
          phoneNumber: responder.phone_number,
          type: 'responders' as ContactTab,
          subtitle: responder.responder_type,
          status: responder.status,
          responder,
        })) || []

    const hospitalContacts = hospitals
      .filter((hospital: any) =>
        matchesCurrentPrecinct(hospital, currentPrecinctId)
      )
      .map((hospital: any, index: number) => ({
        id: hospital.hospital_id || hospital.id || `hospital-${index}`,
        name: hospital.name || hospital.hospital_name || 'Hospital',
        phoneNumber:
          hospital.phoneNumber ||
          hospital.phone_number ||
          hospital.contact?.phone ||
          hospital.contact_phone,
        type: 'hospitals' as ContactTab,
        subtitle: hospital.availability_status || hospital.state,
        status: hospital.availability_status,
      }))

    const ambulanceProviders = Array.isArray((ambulanceData as any)?.items)
      ? (ambulanceData as any).items
      : Array.isArray((ambulanceData as any)?.ambulance_providers)
        ? (ambulanceData as any).ambulance_providers
        : Array.isArray((ambulanceData as any)?.providers)
          ? (ambulanceData as any).providers
          : Array.isArray(ambulanceData)
            ? ambulanceData
            : []

    const ambulanceContacts = ambulanceProviders
      .filter((provider: any) =>
        matchesCurrentPrecinct(provider, currentPrecinctId)
      )
      .map((provider: any, index: number) => ({
        id:
          provider.provider_id ||
          provider.ambulance_provider_id ||
          provider.id ||
          `ambulance-${index}`,
        name:
          provider.provider_name ||
          provider.company_name ||
          provider.name ||
          'Ambulance Provider',
        phoneNumber:
          provider.contact_phone_number ||
          provider.phone_number ||
          provider.contact_phone ||
          provider.dispatch_phone ||
          provider.dispatch_phone_number ||
          provider.contact?.phone,
        type: 'ambulances' as ContactTab,
        subtitle: provider.service_level || provider.status,
        status: provider.status,
      }))

    return [...responderContacts, ...hospitalContacts, ...ambulanceContacts]
  }, [ambulanceData, currentPrecinctId, hospitals, respondersData?.items])

  const visibleContacts = useMemo(() => {
    const query = search.trim().toLowerCase()
    return contacts.filter(contact => {
      const matchesTab = selectedTab === 'all' || contact.type === selectedTab
      const searchable = [contact.name, contact.phoneNumber, contact.subtitle]
        .filter(Boolean)
        .join(' ')
        .toLowerCase()
      return matchesTab && (!query || searchable.includes(query))
    })
  }, [contacts, search, selectedTab])

  const isLoading = loadingResponders || loadingHospitals || loadingAmbulances

  const isContactCallable = (contact: SoftphoneContact) =>
    Boolean(contact.responder || contact.phoneNumber)

  const handleCallContact = (contact: SoftphoneContact) => {
    if (!isContactCallable(contact)) return

    if (contact.responder) {
      void startResponderCall({
        responderId: contact.responder.responder_id,
        displayName: contact.name,
        phoneNumber: contact.phoneNumber,
        caseId: contact.responder.responding_to || undefined,
        status: contact.responder.status,
      })
      return
    }

    if (contact.phoneNumber) {
      void startDialpadCall(contact.phoneNumber)
    }
  }

  return (
    <div className="flex min-h-0 flex-1 flex-col">
      <SearchBox value={search} onChange={setSearch} />
      <div className="mx-4 mt-2 grid shrink-0 grid-cols-4 bg-[#fbfbfb]">
        {[
          { key: 'all', label: 'All' },
          { key: 'responders', label: 'Responders' },
          { key: 'hospitals', label: 'Hospitals' },
          { key: 'ambulances', label: 'Ambulances' },
        ].map(item => (
          <button
            key={item.key}
            onClick={() => setSelectedTab(item.key as ContactTab)}
            className={`h-10 border-b text-xs transition-colors cursor-pointer ${
              selectedTab === item.key
                ? 'border-primary font-medium text-primary'
                : 'border-transparent text-[#777777]'
            } hover:text-primary hover:bg-blue-100/50`}
            style={{
              fontSize: '12px',
              fontWeight: selectedTab === item.key ? 500 : 400,
              color: selectedTab === item.key ? '#254769' : '#797676',
            }}
          >
            {item.label}
          </button>
        ))}
      </div>
      <div className="min-h-0 flex-1 overflow-y-auto px-4 py-3">
        {isLoading ? (
          <div className="flex h-full items-center justify-center">
            <LoaderCircle size={28} className="animate-spin text-primary" />
          </div>
        ) : visibleContacts.length === 0 ? (
          <EmptyState
            title="No contacts"
            subtitle="Contacts will appear here"
            icon={<ContactRound size={50} className="text-[#969696]" />}
          />
        ) : (
          <div className="space-y-2 mb-2">
            {visibleContacts.map(contact => {
              const canCallContact = isContactCallable(contact)
              return (
                <div
                  key={`${contact.type}-${contact.id}`}
                  className="grid grid-cols-[1fr_auto] items-center gap-3 rounded px- py-2"
                >
                  <div className="flex items-center gap-3">
                    <svg
                      width="24"
                      height="24"
                      viewBox="0 0 24 24"
                      fill="none"
                      xmlns="http://www.w3.org/2000/svg"
                    >
                      <path
                        d="M12 10C14.2091 10 16 8.20914 16 6C16 3.79086 14.2091 2 12 2C9.79086 2 8 3.79086 8 6C8 8.20914 9.79086 10 12 10Z"
                        stroke="#254769"
                        strokeWidth="1.5"
                      />
                      <path
                        d="M19.998 18C19.9993 17.836 20 17.6693 20 17.5C20 15.015 16.418 13 12 13C7.582 13 4 15.015 4 17.5C4 19.985 4 22 12 22C14.231 22 15.84 21.843 17 21.563"
                        stroke="#254769"
                        strokeWidth="1.5"
                        strokeLinecap="round"
                      />
                    </svg>
                    <div className="min-w-0 capitalize">
                      <div className="truncate text-sm font-semibold text-[#313030]">
                        {contact.name}
                      </div>
                      <div className="mt-1 truncate text-xs text-[#1A1B23]">
                        {[contact.phoneNumber, contact.subtitle, contact.status]
                          .filter(Boolean)
                          .join(' • ')}
                      </div>
                    </div>
                  </div>
                  <button
                    disabled={!canCallContact}
                    onClick={() => handleCallContact(contact)}
                    title={
                      canCallContact
                        ? `Call ${contact.name}`
                        : 'No callable number available'
                    }
                    className="flex h-9 w-9 items-center justify-center rounded-full hover:bg-[#eaf2ff] text-primary disabled:cursor-not-allowed disabled:opacity-40 cursor-pointer"
                    aria-label={
                      canCallContact
                        ? `Call ${contact.name}`
                        : `${contact.name} cannot be called`
                    }
                  >
                    <Phone size={17} className="text-primary" />
                  </button>
                </div>
              )
            })}
          </div>
        )}
      </div>
    </div>
  )
}

const CallQueueBanner = ({ count }: { count: number }) => {
  if (count <= 0) return null

  return (
    <div
      className="mx-4 mt-3 flex h-10 shrink-0 items-center gap-2 rounded bg-[#fff1e4] px-3 text-[#ff7a1a]"
      title={`${count} inbound call${count === 1 ? '' : 's'} currently waiting in the Asterisk dispatch queue.`}
      aria-label={`${count} inbound call${count === 1 ? '' : 's'} waiting in the dispatch queue`}
    >
      <Clock3 size={17} />
      <span className="text-base font-medium">
        {count} call{count === 1 ? '' : 's'} in queue
      </span>
    </div>
  )
}

const QueueMiniStatus = ({
  queue,
  fallbackCalls,
  fallbackDispatcherPosition,
  fallbackDispatcherCount,
}: {
  queue?: SoftphoneQueueStatus
  fallbackCalls: SoftphoneCallSession[]
  fallbackDispatcherPosition?: number
  fallbackDispatcherCount: number
}) => {
  const fallbackQueuedCalls = fallbackCalls.filter(call =>
    ['initiating', 'dialing', 'ringing', 'answered', 'held'].includes(
      String(call.status)
    )
  )
  const queueCount = queue?.callerCount ?? fallbackQueuedCalls.length
  const dispatcherCount = queue?.dispatcherCount ?? fallbackDispatcherCount
  const dispatcherPosition =
    queue?.dispatcherPosition ?? fallbackDispatcherPosition

  const dispatcherText =
    dispatcherPosition && dispatcherCount
      ? `D ${dispatcherPosition}/${dispatcherCount}`
      : dispatcherCount > 0
        ? `${dispatcherCount} disp`
        : '0 disp'
  const queueSource = queue ? 'Asterisk queue' : 'Local activity fallback'
  const checkedAt = queue?.checkedAt
    ? new Date(queue.checkedAt).toLocaleTimeString([], {
        hour: '2-digit',
        minute: '2-digit',
      })
    : null
  const tooltipLines = [
    `${queueCount} caller${queueCount === 1 ? '' : 's'} waiting`,
    `${dispatcherCount} available dispatcher${dispatcherCount === 1 ? '' : 's'}`,
    dispatcherPosition
      ? `Your queue order: ${dispatcherPosition} of ${dispatcherCount || dispatcherPosition}`
      : 'Your queue order is not active',
    `Source: ${queueSource}`,
    checkedAt ? `Checked ${checkedAt}` : null,
    queue?.degraded
      ? `Limited queue data${queue.error ? `: ${queue.error}` : ''}`
      : null,
  ].filter(Boolean)
  const tooltipText = tooltipLines.join('. ')

  return (
    <div
      tabIndex={0}
      title={tooltipText}
      aria-label={tooltipText}
      className="group relative z-10 flex shrink-0 items-center gap-1 whitespace-nowrap rounded bg-[#F5F5F7] px-2 py-1 text-xs leading-none text-[#6b7280] outline-none focus-visible:ring-2 focus-visible:ring-primary"
    >
      <UsersRound size={12} strokeWidth={2.5} />
      <span>
        Queue{' '}
        <span className="font-semibold">
          {queueCount} - {dispatcherText}
        </span>
      </span>
      <div className="pointer-events-none absolute bottom-full right-0 mb-2 hidden w-64 rounded border border-[#dfe3ea] bg-white p-3 text-left text-xs font-normal leading-5 text-[#4b5563] shadow-lg group-focus:block group-hover:block">
        <p className="font-semibold text-[#333333]">Dispatch queue</p>
        <p className="mt-1">
          {queueCount} caller{queueCount === 1 ? '' : 's'} waiting.{' '}
          {dispatcherCount} dispatcher{dispatcherCount === 1 ? '' : 's'} ready.
        </p>
        <p className="mt-1">
          {dispatcherPosition
            ? `You are ${dispatcherPosition} of ${dispatcherCount || dispatcherPosition} in the active Asterisk queue.`
            : `You are not currently placed in the active Asterisk queue order.`}
        </p>
        <p className="mt-1 text-[#6b7280]">
          {queueSource}
          {checkedAt ? ` - checked ${checkedAt}` : ''}
        </p>
        {queue?.degraded ? (
          <p className="mt-1 text-[#9a4a00]">
            Limited queue data{queue.error ? `: ${queue.error}` : ''}
          </p>
        ) : null}
      </div>
    </div>
  )
}

const CallControlButton = ({
  label,
  icon,
  onClick,
  tone = 'default',
  disabled = false,
}: {
  label: string
  icon: React.ReactNode
  onClick?: () => void
  tone?: 'default' | 'danger' | 'success' | 'warning'
  disabled?: boolean
}) => {
  const toneClass =
    tone === 'danger'
      ? 'border-[#ff5263] bg-[#fff6f7] text-[#ff5263]'
      : tone === 'success'
        ? 'border-[#12a84f] bg-[#f2fff7] text-[#12a84f]'
        : tone === 'warning'
          ? 'border-[#ff8a22] bg-[#fff8f1] text-[#ff8a22]'
          : 'border-[#cfcfcf] bg-white text-[#333333]'

  return (
    <button
      onClick={onClick}
      disabled={disabled}
      className="flex w-[76px] flex-col items-center gap-2 disabled:cursor-not-allowed disabled:opacity-35"
    >
      <span
        className={`flex h-[68px] w-[68px] items-center justify-center rounded-2xl border ${toneClass}`}
      >
        {icon}
      </span>
      <span className="text-sm text-[#4a4a4a]">{label}</span>
    </button>
  )
}

const CallScreen = ({
  queueCount,
  queueStatus,
}: {
  queueCount: number
  queueStatus?: SoftphoneQueueStatus
}) => {
  const {
    activeCall,
    answerIncomingCall,
    hangupCall,
    isAnsweringIncomingCall,
    rejectIncomingCall,
    toggleHold,
    toggleMute,
    transferActiveCall,
  } = useSoftphone()
  const { dispatcher } = useDispatcher()
  const currentPrecinctId = dispatcher?.precinct_id
  const { data: dispatchersData, isLoading: isLoadingDispatchers } =
    useGetAllDispatchers()
  const activeCallPatientPhoneNumbers = useMemo(
    () => getActiveCallPhoneCandidates(activeCall),
    [activeCall]
  )
  const contactLookup = useSoftphoneContactLookup(
    activeCallPatientPhoneNumbers,
    Boolean(activeCall)
  )
  const timer = useCallTimer(activeCall)
  const [showTransfer, setShowTransfer] = useState(false)
  const [transferDestination, setTransferDestination] = useState('')
  const queueAgentByDispatcherId = useMemo(
    () => getQueueAgentLookupByDispatcherId(queueStatus?.agents),
    [queueStatus?.agents]
  )
  const onlineDispatcherOptions = useMemo(
    () =>
      getDispatchersFromResponse(dispatchersData)
        .filter(dispatcherItem =>
          matchesCurrentPrecinct(dispatcherItem, currentPrecinctId)
        )
        .filter(dispatcherItem => {
          const dispatcherId = dispatcherItem.dispatcher_id
          if (!dispatcherId) return false

          return (
            getDispatcherPresenceStatus(
              queueAgentByDispatcherId.get(dispatcherId)
            ) === 'online'
          )
        })
        .map(dispatcherItem => ({
          value: dispatcherItem.dispatcher_id,
          label: getDispatcherDisplayName(dispatcherItem),
        }))
        .sort((a, b) => a.label.localeCompare(b.label)),
    [currentPrecinctId, dispatchersData, queueAgentByDispatcherId]
  )
  const contactDisplay = useMemo(
    () =>
      activeCall
        ? getActiveCallContactDisplay(activeCall, contactLookup)
        : undefined,
    [activeCall, contactLookup]
  )

  React.useEffect(() => {
    if (!transferDestination) return
    if (
      onlineDispatcherOptions.some(
        option => option.value === transferDestination
      )
    ) {
      return
    }

    setTransferDestination('')
  }, [onlineDispatcherOptions, transferDestination])

  if (!activeCall) return null

  const handleTransferCall = () => {
    if (!activeCall?.callId) {
      void transferActiveCall(transferDestination)
      return
    }

    const trimmedDestination = transferDestination.trim()
    if (!trimmedDestination) {
      void transferActiveCall(trimmedDestination)
      return
    }

    void transferActiveCall(trimmedDestination)
  }

  const isIncoming = activeCall.status === 'ringing'
  const isDialing =
    activeCall.status === 'initiating' || activeCall.status === 'dialing'
  const isHeld = activeCall.status === 'held' || activeCall.isHeld
  const isFailed = [
    'failed',
    'completed',
    'cancelled',
    'canceled',
    'rejected',
    'no_answer',
    'missed',
  ].includes(activeCall.status)
  const isRecording = !isFailed
  const connected = activeCall.status === 'answered' || isHeld
  const statusLabel = isIncoming
    ? 'Incoming.....'
    : isDialing
      ? 'Dialing........'
      : isHeld
        ? 'On Hold'
        : isFailed
          ? 'Call Failed'
          : connected
            ? 'Connected'
            : 'Call Ending'
  const statusColor = isFailed
    ? 'text-[#d83a4a]'
    : isHeld
      ? 'text-[#ff8a22]'
      : 'text-[#12a84f]'

  return (
    <div className="flex min-h-0 flex-1 flex-col overflow-y-auto">
      <CallQueueBanner count={queueCount} />

      {isRecording ? (
        <div
          role="status"
          aria-live="polite"
          className="mx-4 mt-3 flex items-center justify-center gap-2 rounded-md border border-[#F5C2C7] bg-[#FFF1F2] px-3 py-2 text-xs font-semibold text-[#B42318]"
        >
          <span
            className="h-2 w-2 shrink-0 rounded-full bg-[#D92D20]"
            aria-hidden="true"
          />
          This call is being recorded
        </div>
      ) : null}

      <div className="flex min-h-[360px] flex-1 flex-col items-center justify-center px-6">
        <div className="mb-8 text-center">
          {isIncoming ? (
            <PhoneIncoming size={22} className="mx-auto text-[#12a84f]" />
          ) : connected ? (
            <UserRound
              size={23}
              className={`mx-auto ${isHeld ? 'text-[#ff8a22]' : 'text-[#12a84f]'}`}
            />
          ) : null}
          <p
            className={`mt-2 text-base font-semibold ${
              isDialing ? 'text-[#333333]' : statusColor
            }`}
          >
            {statusLabel}
          </p>
        </div>

        <p className="max-w-full break-all text-center text-[42px] font-medium leading-tight text-[#4a4a4a]">
          {contactDisplay?.name}
        </p>

        {contactDisplay?.detail ? (
          <p className="mt-3 max-w-full break-all text-center text-base text-[#555555]">
            {contactDisplay.detail}
          </p>
        ) : connected ? (
          <p className="mt-5 text-[26px] font-semibold text-[#666666]">
            {timer}
          </p>
        ) : null}
        {contactDisplay?.detail && connected ? (
          <p className="mt-5 text-[26px] font-semibold text-[#666666]">
            {timer}
          </p>
        ) : null}
      </div>

      <div className="mb-14 flex shrink-0 items-center justify-center gap-5">
        {isIncoming ? (
          <>
            <CallControlButton
              label="Reject"
              tone="danger"
              icon={<PhoneOff size={26} />}
              onClick={() => void rejectIncomingCall()}
            />
            <CallControlButton
              label={isAnsweringIncomingCall ? 'Answering' : 'Answer'}
              tone="success"
              icon={<Phone size={26} />}
              disabled={isAnsweringIncomingCall}
              onClick={() => void answerIncomingCall()}
            />
          </>
        ) : (
          <>
            <CallControlButton
              label={activeCall.isMuted ? 'Unmute' : 'Mute'}
              tone={activeCall.isMuted ? 'danger' : 'default'}
              icon={
                activeCall.isMuted ? <MicOff size={25} /> : <Mic size={25} />
              }
              disabled={isDialing}
              onClick={toggleMute}
            />
            <CallControlButton
              label={isHeld ? 'Resume' : 'Hold'}
              tone={isHeld ? 'warning' : 'default'}
              icon={isHeld ? <Play size={24} /> : <Pause size={24} />}
              disabled={isDialing}
              onClick={() => void toggleHold()}
            />
            <CallControlButton
              label="Transfer"
              icon={<Forward size={25} />}
              disabled={isDialing}
              onClick={() => setShowTransfer(current => !current)}
            />
            <CallControlButton
              label="End Call"
              tone="danger"
              icon={<PhoneOff size={26} />}
              onClick={() => void hangupCall()}
            />
          </>
        )}
      </div>
      {showTransfer && (
        <div className="mx-4 mb-6 flex shrink-0 gap-2">
          <select
            value={transferDestination}
            onChange={event => setTransferDestination(event.target.value)}
            disabled={
              isLoadingDispatchers || onlineDispatcherOptions.length === 0
            }
            className="h-10 min-w-0 flex-1 rounded border border-gray-50 bg-white px-3 text-sm outline-none disabled:cursor-not-allowed disabled:bg-[#F5F5F7] disabled:text-[#777777]"
            aria-label="Transfer destination dispatcher"
          >
            <option value="">
              {isLoadingDispatchers
                ? 'Loading dispatchers...'
                : onlineDispatcherOptions.length > 0
                  ? 'Select online dispatcher'
                  : 'No online dispatchers'}
            </option>
            {onlineDispatcherOptions.map(option => (
              <option key={option.value} value={option.value}>
                {option.label}
              </option>
            ))}
          </select>
          <button
            onClick={handleTransferCall}
            className="h-10 rounded bg-primary px-4 text-sm font-semibold text-white"
          >
            Transfer
          </button>
        </div>
      )}
    </div>
  )
}

const ReadinessBanner = () => {
  const {
    error,
    isEligible,
    readinessMessage,
    registrationStatus,
    realtimeError,
    realtimeStatus,
    retryConnection,
  } = useSoftphone()

  const hasRegistrationIssue = registrationStatus !== 'registered'
  const realtimeMessage =
    isEligible && registrationStatus === 'registered'
      ? getRealtimeStatusMessage(realtimeStatus, realtimeError)
      : null
  const message =
    error || (hasRegistrationIssue ? readinessMessage : realtimeMessage)
  const displayMessage = humanizeSoftphoneMessage(message)

  if (!displayMessage) return null

  const isWarning =
    !isEligible ||
    Boolean(error) ||
    realtimeStatus === 'disconnected' ||
    realtimeStatus === 'error' ||
    realtimeStatus === 'unavailable'

  return (
    <div
      className={`flex min-w-0 max-w-full items-center justify-between gap-2 rounded px-3 py-1 text-xs ${
        isWarning ? 'bg-[#fff1e4] text-[#9a4a00]' : 'bg-[#f4f7fb] text-primary'
      }`}
    >
      <span className="min-w-0 flex-1 truncate" title={message || undefined}>
        {displayMessage}
      </span>
      <button
        onClick={retryConnection}
        className="shrink-0 rounded p-1 hover:bg-white/60 cursor-pointer"
        aria-label="Retry softphone connection"
      >
        <RefreshCw size={12} />
      </button>
    </div>
  )
}

interface SoftphoneWidgetProps {
  anchorRef: RefObject<HTMLElement | null>
}

export const SoftphoneWidget = ({ anchorRef }: SoftphoneWidgetProps) => {
  const { activeCall, activeTab, closeWidget, isEligible, isOpen } =
    useSoftphone()
  const { dispatcher } = useDispatcher()
  const [selectedHistoryCall, setSelectedHistoryCall] =
    useState<SoftphoneCallSession | null>(null)
  const { data: activeCallSummary } = useActiveSoftphoneCalls(
    100,
    isOpen && isEligible
  )
  const { data: queueStatus } = useSoftphoneQueueStatus(
    dispatcher?.dispatcher_id,
    isOpen && isEligible
  )
  const { data: shiftsData } = useShifts(
    {
      limit: 100,
      sortBy: 'from',
      sortOrder: 'desc',
    },
    {
      enabled: isOpen && isEligible,
      refetchInterval: 60 * 1000,
    }
  )
  const activeDispatcherShifts = useMemo(
    () =>
      (shiftsData?.items || [])
        .filter(
          shift =>
            shift.status === 'in-progress' &&
            shift.clock_in_status &&
            !shift.clock_out_status
        )
        .sort((a, b) =>
          String(a.clock_in_time || a.from).localeCompare(
            String(b.clock_in_time || b.from)
          )
        ),
    [shiftsData?.items]
  )
  const dispatcherQueuePosition = activeDispatcherShifts.findIndex(
    shift => shift.dispatcher_id === dispatcher?.dispatcher_id
  )
  const activeCalls = activeCallSummary?.calls || []
  const effectiveQueueStatus = queueStatus || activeCallSummary?.queue
  const fallbackQueueCount = activeCalls.length || (activeCall ? 1 : 0)
  const queueCount = effectiveQueueStatus?.callerCount ?? fallbackQueueCount

  const [position, setPosition] = useState({
    top: 0,
    right: SOFTPHONE_VIEWPORT_MARGIN,
    height: SOFTPHONE_PANEL_MAX_HEIGHT,
  })

  useLayoutEffect(() => {
    if (!isOpen || !anchorRef.current) return

    const updatePosition = () => {
      const rect = anchorRef.current?.getBoundingClientRect()
      if (!rect) return

      const top = rect.bottom + SOFTPHONE_PANEL_GAP
      const panelWidth = Math.min(
        SOFTPHONE_PANEL_WIDTH,
        window.innerWidth - SOFTPHONE_VIEWPORT_MARGIN * 2
      )
      const rawRight = window.innerWidth - rect.right
      const maxRight = Math.max(
        SOFTPHONE_VIEWPORT_MARGIN,
        window.innerWidth - panelWidth - SOFTPHONE_VIEWPORT_MARGIN
      )
      const right = Math.min(
        Math.max(SOFTPHONE_VIEWPORT_MARGIN, rawRight),
        maxRight
      )
      const height = Math.max(
        0,
        Math.min(
          SOFTPHONE_PANEL_MAX_HEIGHT,
          window.innerHeight - top - SOFTPHONE_VIEWPORT_MARGIN
        )
      )

      setPosition({ top, right, height })
    }

    updatePosition()

    // Update position on scroll or resize
    window.addEventListener('scroll', updatePosition, true)
    window.addEventListener('resize', updatePosition)

    return () => {
      window.removeEventListener('scroll', updatePosition, true)
      window.removeEventListener('resize', updatePosition)
    }
  }, [isOpen, anchorRef])

  React.useEffect(() => {
    if (!isOpen || activeTab !== 'history' || activeCall) {
      setSelectedHistoryCall(null)
    }
  }, [activeCall, activeTab, isOpen])

  if (!isOpen) return null

  return (
    <aside
      className="font-family-sans fixed z-90 flex w-[472px] max-w-[calc(100vw-2rem)] flex-col overflow-hidden rounded-lg border border-[#dfdfdf] bg-white shadow-2xl"
      style={{
        top: `${position.top}px`,
        right: `${position.right}px`,
        height: `${position.height}px`,
      }}
    >
      <div className="font-family-heading flex shrink-0 items-center justify-between border-b border-[#dfdfdf] px-4 py-3">
        <div className="text-lg font-bold text-[#413F3F]">Soft Phone</div>
        <button
          onClick={closeWidget}
          className="p-1 rounded-lg hover:bg-gray-100 cursor-pointer"
          aria-label="Close softphone"
        >
          <X className="h-5 w-5" />
        </button>
      </div>

      {activeCall ? (
        <CallScreen
          queueCount={queueCount}
          queueStatus={effectiveQueueStatus}
        />
      ) : (
        <>
          {selectedHistoryCall ? null : <SoftphoneTopTabs />}
          <div className="relative flex min-h-0 flex-1 flex-col pb-7">
            {activeTab === 'history' && (
              <HistoryView
                selectedCall={selectedHistoryCall}
                onSelectCall={setSelectedHistoryCall}
                onBackFromDetails={() => setSelectedHistoryCall(null)}
              />
            )}
            {activeTab === 'dialpad' && <DialpadView />}
            {activeTab === 'contacts' && <ContactsView />}
            <div className="absolute bottom-0 grid w-full grid-cols-[minmax(0,1fr)_auto] items-center gap-2 bg-white p-2">
              <div className="min-w-0">
                <ReadinessBanner />
              </div>
              <div className="justify-self-end">
                <QueueMiniStatus
                  queue={effectiveQueueStatus}
                  fallbackCalls={activeCalls}
                  fallbackDispatcherPosition={
                    dispatcherQueuePosition >= 0
                      ? dispatcherQueuePosition + 1
                      : undefined
                  }
                  fallbackDispatcherCount={activeDispatcherShifts.length}
                />
              </div>
            </div>
          </div>
        </>
      )}
    </aside>
  )
}

export const SoftphoneHeaderButton = () => {
  const {
    activeCall,
    isEligible,
    openWidget,
    registrationStatus,
    realtimeError,
    realtimeStatus,
    readinessMessage,
  } = useSoftphone()

  const buttonRef = useRef<HTMLButtonElement>(null)
  const isRegistered = registrationStatus === 'registered'
  const isRealtimeConnected = realtimeStatus === 'connected'
  const realtimeMessage = getRealtimeStatusMessage(
    realtimeStatus,
    realtimeError
  )
  const buttonTitle =
    isRegistered && !isRealtimeConnected && realtimeMessage
      ? `${readinessMessage}. ${realtimeMessage}`
      : readinessMessage

  const dotClass = activeCall
    ? 'bg-[#ff5263]'
    : !isEligible
      ? 'bg-[#9ca3af]'
      : registrationStatus === 'error' || registrationStatus === 'unavailable'
        ? 'bg-[#ff5263]'
        : isRegistered
          ? 'bg-[#12a84f]'
          : 'bg-[#ffb020]'

  return (
    <div className="relative">
      <button
        ref={buttonRef}
        onClick={() => openWidget(activeCall ? 'history' : undefined)}
        className="relative rounded-full p-2 text-gray-600 transition-colors hover:bg-gray-100 hover:text-gray-900 cursor-pointer"
        aria-label="Open softphone"
        title={buttonTitle}
      >
        <div className="relative flex h-10 w-10 items-center justify-center rounded-full bg-gray-40">
          {registrationStatus === 'connecting' ? (
            <LoaderCircle size={20} className="animate-spin text-primary" />
          ) : activeCall ? (
            <PhoneCall size={20} className="text-[#ff5263]" />
          ) : (
            <Phone size={20} className="text-primary" />
          )}
          <span
            className={`absolute right-1 top-1 h-2.5 w-2.5 rounded-full border border-white ${dotClass}`}
          />
        </div>
      </button>
      <SoftphoneWidget anchorRef={buttonRef} />
    </div>
  )
}
